import React, { useEffect, useMemo, useState } from 'react';
import api from '../services/api';
import {
  Container, Paper, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Button, TextField, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Alert,
  Menu, MenuItem, IconButton, Box, Stack, TableSortLabel, InputAdornment, CircularProgress, Grid, Card, CardContent,
  Snackbar, LinearProgress, Chip, Switch, Tabs, Tab, Select, Checkbox, ListItemText, Autocomplete, Divider,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SearchIcon from '@mui/icons-material/Search';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { formatAdminLocalDateTime, parseServerUtcDateTime } from '../utils/DocumentUtils';
import { useAuth } from '../contexts/AuthContext';
import { useFeatureFlags } from '../contexts/FeatureFlagsContext';
import PeopleIcon from '@mui/icons-material/People';
import LockIcon from '@mui/icons-material/Lock';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import GoogleIcon from '@mui/icons-material/Google';
import PersonIcon from '@mui/icons-material/Person';
import AuditLogsTable from '../components/admin/AuditLogsTable';
import DocumentActivityTable from '../components/admin/DocumentActivityTable';
import InfoIcon from '@mui/icons-material/Info';
import StorageIcon from '@mui/icons-material/Storage';
import MemoryIcon from '@mui/icons-material/Memory';
import DescriptionIcon from '@mui/icons-material/Description';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import SpeedIcon from '@mui/icons-material/Speed';
import ErrorIcon from '@mui/icons-material/Error';
import StorageOutlinedIcon from '@mui/icons-material/StorageOutlined';
import CloudQueueIcon from '@mui/icons-material/CloudQueue';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import PermMediaOutlinedIcon from '@mui/icons-material/PermMediaOutlined';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CampaignIcon from '@mui/icons-material/Campaign';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import type {
  AnnouncementAudience,
  AnnouncementSeverity,
  CreateSystemAnnouncementRequest,
  SystemAnnouncement,
} from '../types/SystemAnnouncement';
import {
  ANNOUNCEMENT_TEMPLATES,
  AnnouncementTemplateId,
  AnnouncementFormState,
  buildAnnouncementFormFromTemplate,
  buildMaintenanceMessage,
  buildSystemRestartMessage,
  buildNewFeatureMessage,
  emptyAnnouncementForm,
} from '../constants/announcementTemplates';

interface User {
  id: number;
  username: string;
  email: string;
  firstName?: string;
  lastName?: string;
  creationDate: string;
  expirationDate: string;
  lastLoginAt: string;
  provider: string;
  admin: boolean;
  accountNeverExpires: boolean;
  enabled?: boolean;
  emailVerified?: boolean;
  documentCount?: number;
  tierId?: number | null;
  tierName?: string | null;
}

type OptionalUserColumn = 'email' | 'name' | 'documents' | 'emailVerified' | 'enabled' | 'tier';

const OPTIONAL_USER_COLUMNS: { key: OptionalUserColumn; label: string }[] = [
  { key: 'email', label: 'Email' },
  { key: 'name', label: 'Name' },
  { key: 'documents', label: 'Documents' },
  { key: 'emailVerified', label: 'Email verified' },
  { key: 'enabled', label: 'Account status' },
  { key: 'tier', label: 'Tier' },
];

const ADMIN_USER_COLUMNS_STORAGE_KEY = 'adminUserTableOptionalColumns';
const ADMIN_TAB_DOCUMENT_ACTIVITY = 5;
const ADMIN_TAB_TIERS = 9;

type DocumentActivityUserOption =
  | { scope: 'all'; label: 'All users' }
  | { scope: 'user'; user: User };

const ALL_DOCUMENT_ACTIVITY_USERS: DocumentActivityUserOption = {
  scope: 'all',
  label: 'All users',
};

function loadOptionalUserColumns(): Set<OptionalUserColumn> {
  try {
    const raw = localStorage.getItem(ADMIN_USER_COLUMNS_STORAGE_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw) as OptionalUserColumn[];
    return new Set(parsed.filter((k) => OPTIONAL_USER_COLUMNS.some((c) => c.key === k)));
  } catch {
    return new Set();
  }
}

function formatUserDisplayName(user: User): string {
  const parts = [user.firstName, user.lastName].filter(Boolean);
  return parts.length > 0 ? parts.join(' ') : '—';
}

function formatApiErrorMessage(data: unknown, fallback: string): string {
  if (typeof data === 'string' && data.trim()) {
    return data;
  }
  if (data && typeof data === 'object') {
    const message = (data as { message?: unknown }).message;
    if (typeof message === 'string' && message.trim()) {
      return message;
    }
  }
  return fallback;
}

interface OnlineUser {
  id: number;
  username: string;
  email: string;
  loginTime: string;
  provider: string;
}

interface UserDetails {
  id: number;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  avatarUrl: string | null;
  expirationDate: string | null;
  lastLoginAt: string | null;
  admin: boolean;
  documentCount: number;
  enabled?: boolean;
  roles?: string[];
  provider?: string;
  creationDate?: string;
  accountNeverExpires?: boolean;
}

type Order = 'asc' | 'desc';
type SortField = 'username' | 'creationDate' | 'expirationDate' | 'lastLoginAt';

interface SortConfig {
  field: SortField;
  order: Order;
}

type OpenDocumentSortField = 'name' | 'user' | 'creationDate' | 'updatedAt';

interface OpenDocumentSortConfig {
  field: OpenDocumentSortField;
  order: Order;
}

type UserDocumentSortField =
  | 'name'
  | 'revisionCount'
  | 'latestRevisionAt'
  | 'creationDate'
  | 'updatedAt';

interface UserDocumentSortConfig {
  field: UserDocumentSortField;
  order: Order;
}

interface UserStats {
  totalUsers: number;
  activeUsers: number;
  expiredUsers: number;
  adminUsers: number;
  regularUsers: number;
  usersByProvider: {
    LOCAL: number;
    GOOGLE: number;
  };
  redisDbSize: number;
  redisMemoryUsage: number;
  totalDocuments: number;
  totalRevisions?: number;
  totalRevisionBytes?: number;
  totalMediaFiles?: number;
  totalMediaBytes?: number;
  mediaStagingBytes?: number;
  mediaByContentBytes?: number;
  postgresDatabaseBytes?: number;
  postgresDocumentRevisionsRelationBytes?: number;
  mediaVolumeTotalBytes?: number;
  mediaVolumeUsableBytes?: number;
  mediaVolumeUsedBytes?: number;
  mediaStagingRetentionDays?: number;
  mediaStagingPurgeEnabled?: boolean;
  newDocuments24h: number;
  newDocuments7d: number;
  newDocuments30d: number;
  newUsers24h: number;
  newUsers7d: number;
  newUsers30d: number;
  activeSessions: number;
  averageSessionDuration: number;
}

interface SystemHealth {
  cpuUsage: number;
  memoryUsage: number;
  jvmMemoryUsage: number;
  threadCount: number;
  databaseStatus: 'healthy' | 'degraded' | 'unhealthy';
  redisStatus: 'healthy' | 'degraded' | 'unhealthy';
  embeddingStatus: 'healthy' | 'degraded' | 'unhealthy';
  apiResponseTime: number;
  embeddingError?: string;
  embeddingMessage?: string;
  openaiNamingModel?: string;
  openaiStatus?: string;
  openaiLastSuccessAt?: string;
  openaiLastErrorAt?: string;
  openaiLastErrorMessage?: string;
  openaiLastErrorCode?: string;
  openaiConsecutiveErrors?: number;
  openaiTotalErrors?: number;
  openaiApiKeyHint?: string;
}

function formatOpenAiStatus(status?: string): string {
  switch (status) {
    case 'ok':
      return 'OK';
    case 'quota_exceeded':
      return 'Quota exceeded';
    case 'rate_limited':
      return 'Rate limited';
    case 'auth_error':
      return 'API key invalid';
    case 'error':
      return 'Error';
    case 'unknown':
      return 'No calls yet';
    default:
      return status ?? 'Unknown';
  }
}

function openAiStatusChipColor(status?: string): 'success' | 'warning' | 'error' | 'default' {
  if (status === 'ok') return 'success';
  if (status === 'quota_exceeded' || status === 'auth_error') return 'error';
  if (status === 'rate_limited' || status === 'error') return 'warning';
  return 'default';
}

function formatServerLocal(
  value: string | number[] | null | undefined,
  pattern: string,
  empty = '—',
): string {
  const date = parseServerUtcDateTime(value);
  if (!date) {
    return empty;
  }
  const formatted = dayjs(date).format(pattern);
  return formatted;
}

function serverDateTimeDayjs(value: string | number[] | null | undefined): Dayjs {
  const date = parseServerUtcDateTime(value);
  return date ? dayjs(date) : dayjs(Number.NaN);
}

function compareServerDateTimes(
  a: string | number[] | null | undefined,
  b: string | number[] | null | undefined,
  order: number,
): number {
  const aTime = parseServerUtcDateTime(a)?.getTime();
  const bTime = parseServerUtcDateTime(b)?.getTime();
  if (aTime == null && bTime == null) return 0;
  if (aTime == null) return 1;
  if (bTime == null) return -1;
  return order * (aTime - bTime);
}

function formatHealthTimestamp(iso?: string): string {
  if (!iso) return '—';
  const d = serverDateTimeDayjs(iso);
  return d.isValid() ? d.format('MMM D, YYYY h:mm A') : iso;
}

interface OpenDocument {
  id: string;
  name: string;
  user: {
    id: number;
    username: string;
    email: string;
  };
  creationDate: string;
  updatedAt: string;
}

interface UserDocument {
  id: string;
  name: string;
  creationDate: string;
  updatedAt: string;
  revisionCount?: number;
  latestRevisionAt?: string;
}

interface DocumentRevisionRow {
  id: number;
  createdAt: any;
  documentRevision: number;
  sizeBytes: number;
}

interface AdminFeatureFlagRow {
  key: string;
  label: string;
  description: string;
  enabled: boolean;
  storedEnabled: boolean;
  aiFeature: boolean;
  disabledByMaster: boolean;
}

interface TierSummary {
  id: number;
  name: string;
  description?: string;
  userCount: number;
}

interface TierFeatureFlagRow {
  key: string;
  label: string;
  description: string;
  aiFeature: boolean;
  /** null = tier inherits the global value. */
  override: boolean | null;
  effective: boolean;
  globalEnabled: boolean;
}

interface TierLimitRow {
  key: string;
  label: string;
  description: string;
  value: number;
  systemDefault: number;
}

interface LimitDefaultRow {
  key: string;
  label: string;
  description: string;
  value: number;
}

const COLORS = ['#1976d2', '#db4437'];

function clampStoragePercent(numerator: number, denominator: number): number {
  if (!Number.isFinite(numerator) || !Number.isFinite(denominator) || denominator <= 0) return 0;
  return Math.min(100, Math.max(0, (numerator / denominator) * 100));
}

const AdminConsolePage: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [onlineUsers, setOnlineUsers] = useState<OnlineUser[]>([]);
  const [openDocuments, setOpenDocuments] = useState<OpenDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editUser, setEditUser] = useState<User | null>(null);
  const [newExpiration, setNewExpiration] = useState('');
  const [deleteUser, setDeleteUser] = useState<User | null>(null);
  const [resetPasswordUser, setResetPasswordUser] = useState<User | null>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [stagingPurgeDialogOpen, setStagingPurgeDialogOpen] = useState(false);
  const [stagingPurgeLoading, setStagingPurgeLoading] = useState(false);
  const [stagingPurgeRetentionDaysInput, setStagingPurgeRetentionDaysInput] = useState('7');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [sortConfig, setSortConfig] = useState<SortConfig>({ field: 'username', order: 'asc' });
  const [searchTerm, setSearchTerm] = useState('');
  const [usersSubTab, setUsersSubTab] = useState(0);
  const [visibleOptionalColumns, setVisibleOptionalColumns] = useState<Set<OptionalUserColumn>>(loadOptionalUserColumns);
  const [openDocumentsSearch, setOpenDocumentsSearch] = useState('');
  const [openDocumentsSortConfig, setOpenDocumentsSortConfig] = useState<OpenDocumentSortConfig>({
    field: 'updatedAt',
    order: 'desc',
  });
  const [reloadCacheDoc, setReloadCacheDoc] = useState<OpenDocument | null>(null);
  const [reloadCacheLoading, setReloadCacheLoading] = useState(false);
  const [saveToDbDoc, setSaveToDbDoc] = useState<OpenDocument | null>(null);
  const [saveToDbLoading, setSaveToDbLoading] = useState(false);
  const [viewDetailsUser, setViewDetailsUser] = useState<UserDetails | null>(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [detailsError, setDetailsError] = useState<string | null>(null);
  const [stats, setStats] = useState<UserStats | null>(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error'
  });
  const [systemHealth, setSystemHealth] = useState<SystemHealth | null>(null);
  const [sanityReport, setSanityReport] = useState<any>(null);
  const [loadingSanity, setLoadingSanity] = useState(false);
  const [adminTab, setAdminTab] = useState(0);
  const navigate = useNavigate();
  const { user: currentUser, startImpersonation } = useAuth();
  const [impersonatingUserId, setImpersonatingUserId] = useState<number | null>(null);
  const [viewingDocumentKey, setViewingDocumentKey] = useState<string | null>(null);
  const { refresh: refreshPublicFeatureFlags } = useFeatureFlags();
  const [userDocuments, setUserDocuments] = useState<UserDocument[]>([]);
  const [userDocumentsSortConfig, setUserDocumentsSortConfig] = useState<UserDocumentSortConfig>({
    field: 'updatedAt',
    order: 'desc',
  });
  const [viewDocumentsUser, setViewDocumentsUser] = useState<User | null>(null);
  const [activityUserSelection, setActivityUserSelection] =
    useState<DocumentActivityUserOption | null>(null);
  const [deleteDocument, setDeleteDocument] = useState<UserDocument | null>(null);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [viewRevisionsDocument, setViewRevisionsDocument] = useState<UserDocument | null>(null);
  const [documentRevisions, setDocumentRevisions] = useState<DocumentRevisionRow[]>([]);
  const [loadingRevisions, setLoadingRevisions] = useState(false);
  const [documentActionsAnchorEl, setDocumentActionsAnchorEl] = useState<null | HTMLElement>(null);
  const [documentActionsDoc, setDocumentActionsDoc] = useState<UserDocument | null>(null);
  const [copyingDocumentId, setCopyingDocumentId] = useState<string | null>(null);
  const [copyingRevisionId, setCopyingRevisionId] = useState<number | null>(null);
  const [restoringRevisionId, setRestoringRevisionId] = useState<number | null>(null);
  const [restoreRevisionConfirm, setRestoreRevisionConfirm] = useState<DocumentRevisionRow | null>(null);
  const [repairingId, setRepairingId] = useState<string | null>(null);
  const [repairingAll, setRepairingAll] = useState(false);
  const [repairAllProgress, setRepairAllProgress] = useState<{
    completed: number;
    total: number;
    currentCheck?: string;
    phase: 'repairing' | 'refreshing';
  } | null>(null);
  const [featureFlagRows, setFeatureFlagRows] = useState<AdminFeatureFlagRow[]>([]);
  const [featureFlagsSavingKey, setFeatureFlagsSavingKey] = useState<string | null>(null);
  const [tiers, setTiers] = useState<TierSummary[]>([]);
  const [selectedTierId, setSelectedTierId] = useState<number | null>(null);
  const [tierFlags, setTierFlags] = useState<TierFeatureFlagRow[]>([]);
  const [tierUsers, setTierUsers] = useState<string[]>([]);
  const [tierFlagSavingKey, setTierFlagSavingKey] = useState<string | null>(null);
  const [tierLimits, setTierLimits] = useState<TierLimitRow[]>([]);
  const [tierLimitDrafts, setTierLimitDrafts] = useState<Record<string, string>>({});
  const [tierLimitSavingKey, setTierLimitSavingKey] = useState<string | null>(null);
  const [limitDefaultRows, setLimitDefaultRows] = useState<LimitDefaultRow[]>([]);
  const [limitDefaultDrafts, setLimitDefaultDrafts] = useState<Record<string, string>>({});
  const [limitDefaultSavingKey, setLimitDefaultSavingKey] = useState<string | null>(null);
  const [newTierName, setNewTierName] = useState('');
  const [newTierDescription, setNewTierDescription] = useState('');
  const [creatingTier, setCreatingTier] = useState(false);
  const [assignUserId, setAssignUserId] = useState<number | ''>('');
  const [assignTierId, setAssignTierId] = useState<number | ''>('');
  const [assigningTier, setAssigningTier] = useState(false);
  const [setTierUser, setSetTierUser] = useState<User | null>(null);
  const [setTierSelection, setSetTierSelection] = useState<number | ''>('');
  const [savingUserTier, setSavingUserTier] = useState(false);
  const [openaiProbing, setOpenaiProbing] = useState(false);
  const [openaiProbeMessage, setOpenaiProbeMessage] = useState<{ severity: 'success' | 'error'; text: string } | null>(null);
  const [announcements, setAnnouncements] = useState<SystemAnnouncement[]>([]);
  const [announcementsLoading, setAnnouncementsLoading] = useState(false);
  const [createAnnouncementOpen, setCreateAnnouncementOpen] = useState(false);
  const [announcementSaving, setAnnouncementSaving] = useState(false);
  const [showAnnouncementHistory, setShowAnnouncementHistory] = useState(false);
  const [deleteAnnouncement, setDeleteAnnouncement] = useState<SystemAnnouncement | null>(null);
  const [announcementTemplateId, setAnnouncementTemplateId] = useState<AnnouncementTemplateId | ''>('');
  const [announcementForm, setAnnouncementForm] = useState<AnnouncementFormState>(emptyAnnouncementForm());

  const fetchUsers = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/admin/users');
      console.log('User data:', res.data);
      setUsers(res.data);
    } catch (err) {
      setError('Failed to fetch users.');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/admin/statistics');
      setStats(response.data);
    } catch (err) {
      console.error('Failed to fetch statistics:', err);
    }
  };

  useEffect(() => {
    if (!stagingPurgeDialogOpen || !stats) return;
    const d =
      stats.mediaStagingRetentionDays != null && stats.mediaStagingRetentionDays > 0
        ? stats.mediaStagingRetentionDays
        : 7;
    setStagingPurgeRetentionDaysInput(String(d));
  }, [stagingPurgeDialogOpen, stats]);

  const confirmStagingPurge = async () => {
    setStagingPurgeLoading(true);
    setError('');
    try {
      const parsed = parseInt(stagingPurgeRetentionDaysInput.trim(), 10);
      const retentionDays = Number.isFinite(parsed) ? Math.min(3650, Math.max(1, parsed)) : 7;
      const res = await api.post<{
        purgedStagingObjects?: number;
        retentionDaysUsed?: number;
        retentionOverridden?: boolean;
      }>('/admin/content-media/purge-staging', null, { params: { retentionDays } });
      const n = res.data?.purgedStagingObjects ?? 0;
      const d = res.data?.retentionDaysUsed ?? retentionDays;
      setSuccess(`Removed ${n} stale staging object(s) (last modified older than ${d} day(s)).`);
      setStagingPurgeDialogOpen(false);
      await fetchStats();
    } catch (e: unknown) {
      const ax = e as { response?: { data?: unknown } };
      const data = ax?.response?.data;
      const msg = formatApiErrorMessage(ax?.response?.data, 'Failed to purge staging.');
      setError(msg);
    } finally {
      setStagingPurgeLoading(false);
    }
  };

  const fetchOnlineUsers = async () => {
    try {
      const response = await api.get('/admin/online-users');
      setOnlineUsers(response.data);
    } catch (err) {
      console.error('Failed to fetch online users:', err);
    }
  };

  const fetchSystemHealth = async () => {
    try {
      const startTime = performance.now();
      const response = await api.get('/admin/system-health');
      const endTime = performance.now();
      const responseTime = endTime - startTime;
      
      setSystemHealth({
        ...response.data,
        apiResponseTime: responseTime
      });
    } catch (err) {
      console.error('Failed to fetch system health:', err);
    }
  };

  const runOpenAiProbe = async () => {
    setOpenaiProbing(true);
    setOpenaiProbeMessage(null);
    try {
      const { data } = await api.post<{
        ok?: boolean;
        reply?: string;
        model?: string;
        api_key_hint?: string;
      }>('/admin/embedding/openai-probe');
      const hint = data.api_key_hint ? ` (key ${data.api_key_hint})` : '';
      setOpenaiProbeMessage({
        severity: 'success',
        text: `OpenAI OK — ${data.model ?? 'model'} replied: "${data.reply ?? 'OK'}"${hint}`,
      });
      await fetchSystemHealth();
    } catch (e: unknown) {
      const ax = e as { response?: { data?: unknown } };
      const raw = ax?.response?.data;
      let text = 'OpenAI probe failed.';
      if (typeof raw === 'string') {
        try {
          const parsed = JSON.parse(raw) as { detail?: { message?: string; api_key_hint?: string } | string };
          const detail = parsed.detail;
          if (typeof detail === 'object' && detail?.message) {
            text = detail.message;
            if (detail.api_key_hint) text += ` Key in use: ${detail.api_key_hint}.`;
          } else if (typeof detail === 'string') {
            text = detail;
          }
        } catch {
          text = raw;
        }
      } else if (raw && typeof raw === 'object') {
        const d = raw as { detail?: { message?: string; api_key_hint?: string }; message?: string };
        if (d.detail && typeof d.detail === 'object' && d.detail.message) {
          text = d.detail.message;
          if (d.detail.api_key_hint) text += ` Key in use: ${d.detail.api_key_hint}.`;
        } else if (d.message) {
          text = d.message;
        }
      }
      setOpenaiProbeMessage({ severity: 'error', text });
      await fetchSystemHealth();
    } finally {
      setOpenaiProbing(false);
    }
  };

  const fetchOpenDocuments = async () => {
    try {
      const response = await api.get('/admin/open-documents');
      setOpenDocuments(response.data);
    } catch (err) {
      console.error('Failed to fetch open documents:', err);
    }
  };

  const fetchSanityReport = async () => {
    setLoadingSanity(true);
    try {
      const response = await api.get('/admin/sanity-checks');
      setSanityReport(response.data);
    } catch (err: any) {
      setSanityReport(null);
      setError(formatApiErrorMessage(err?.response?.data, 'Failed to load sanity check report.'));
    } finally {
      setLoadingSanity(false);
    }
  };

  const fetchFeatureFlags = async () => {
    try {
      const response = await api.get<AdminFeatureFlagRow[]>('/admin/feature-flags');
      setFeatureFlagRows(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('Failed to fetch feature flags:', err);
      setFeatureFlagRows([]);
    }
  };

  const fetchAnnouncements = async () => {
    setAnnouncementsLoading(true);
    try {
      const response = await api.get<SystemAnnouncement[]>('/admin/announcements');
      setAnnouncements(Array.isArray(response.data) ? response.data : []);
    } catch (err: unknown) {
      setError(formatApiErrorMessage((err as any)?.response?.data, 'Failed to fetch announcements.'));
    } finally {
      setAnnouncementsLoading(false);
    }
  };

  useEffect(() => {
    if (adminTab === 2) {
      void fetchFeatureFlags();
      void fetchLimitDefaults();
    }
    if (adminTab === ADMIN_TAB_DOCUMENT_ACTIVITY) {
      void fetchUsers();
    }
    if (adminTab === 7) {
      void fetchAnnouncements();
    }
    if (adminTab === ADMIN_TAB_TIERS) {
      void fetchTiers();
      void fetchUsers();
    }
  }, [adminTab]);

  useEffect(() => {
    if (adminTab === ADMIN_TAB_TIERS && selectedTierId != null) {
      void fetchTierFlags(selectedTierId);
      void fetchTierLimits(selectedTierId);
      void fetchTierUsers(selectedTierId);
    }
  }, [adminTab, selectedTierId]);

  const activityUserOptions = useMemo((): DocumentActivityUserOption[] => {
    const sorted = [...users].sort((a, b) => a.username.localeCompare(b.username));
    return [ALL_DOCUMENT_ACTIVITY_USERS, ...sorted.map((user) => ({ scope: 'user' as const, user }))];
  }, [users]);

  const resetAnnouncementForm = () => {
    setAnnouncementTemplateId('');
    setAnnouncementForm(emptyAnnouncementForm());
  };

  const handleAnnouncementTemplateChange = (templateId: AnnouncementTemplateId | '') => {
    setAnnouncementTemplateId(templateId);
    if (!templateId) {
      setAnnouncementForm(emptyAnnouncementForm());
      return;
    }
    setAnnouncementForm(buildAnnouncementFormFromTemplate(templateId));
  };

  const handleAnnouncementWindowChange = (field: 'startsAt' | 'endsAt', value: string) => {
    setAnnouncementForm((prev) => {
      const next = { ...prev, [field]: value };
      if (announcementTemplateId === 'maintenance' && next.startsAt && next.endsAt) {
        next.message = buildMaintenanceMessage(next.startsAt, next.endsAt);
      }
      if (announcementTemplateId === 'systemRestart' && next.startsAt && next.endsAt) {
        next.message = buildSystemRestartMessage(next.startsAt, next.endsAt);
      }
      return next;
    });
  };

  const handleFeatureNameChange = (featureName: string) => {
    setAnnouncementForm((prev) => {
      const { title, message } = buildNewFeatureMessage(featureName);
      return { ...prev, featureName, title, message };
    });
  };

  const handleCreateAnnouncement = async () => {
    setAnnouncementSaving(true);
    setError('');
    setSuccess('');
    try {
      const payload: CreateSystemAnnouncementRequest = {
        title: announcementForm.title.trim() || undefined,
        message: announcementForm.message.trim(),
        severity: announcementForm.severity,
        audience: announcementForm.audience,
        targetUsernames:
          announcementForm.audience === 'SPECIFIC'
            ? announcementForm.targetUsernames
                .split(',')
                .map((s) => s.trim())
                .filter(Boolean)
            : undefined,
        startsAt: announcementForm.startsAt.trim()
          ? dayjs(announcementForm.startsAt).format('YYYY-MM-DDTHH:mm:ss')
          : undefined,
        endsAt: announcementForm.endsAt.trim()
          ? dayjs(announcementForm.endsAt).format('YYYY-MM-DDTHH:mm:ss')
          : undefined,
        dismissible: announcementForm.dismissible,
      };
      await api.post('/admin/announcements', payload);
      setSuccess('Announcement published.');
      setCreateAnnouncementOpen(false);
      resetAnnouncementForm();
      await fetchAnnouncements();
    } catch (err: unknown) {
      setError(formatApiErrorMessage((err as any)?.response?.data, 'Failed to publish announcement.'));
    } finally {
      setAnnouncementSaving(false);
    }
  };

  const handleDeactivateAnnouncement = async (id: number) => {
    setError('');
    setSuccess('');
    try {
      await api.post(`/admin/announcements/${id}/deactivate`);
      setSuccess('Announcement deactivated.');
      await fetchAnnouncements();
    } catch (err: unknown) {
      setError(formatApiErrorMessage((err as any)?.response?.data, 'Failed to deactivate announcement.'));
    }
  };

  const handleReactivateAnnouncement = async (id: number) => {
    setError('');
    setSuccess('');
    try {
      await api.post(`/admin/announcements/${id}/reactivate`);
      setSuccess('Announcement reactivated. Users who dismissed it will see it again.');
      await fetchAnnouncements();
    } catch (err: unknown) {
      setError(formatApiErrorMessage((err as any)?.response?.data, 'Failed to reactivate announcement.'));
    }
  };

  const handleDeleteAnnouncement = async () => {
    if (!deleteAnnouncement) return;
    setError('');
    setSuccess('');
    try {
      await api.delete(`/admin/announcements/${deleteAnnouncement.id}`);
      setSuccess('Announcement deleted.');
      setDeleteAnnouncement(null);
      await fetchAnnouncements();
    } catch (err: unknown) {
      setError(formatApiErrorMessage((err as any)?.response?.data, 'Failed to delete announcement.'));
    }
  };

  const getAnnouncementStatus = (announcement: SystemAnnouncement) => {
    const now = dayjs();
    const starts = announcement.startsAt ? serverDateTimeDayjs(announcement.startsAt) : null;
    const ends = announcement.endsAt ? serverDateTimeDayjs(announcement.endsAt) : null;
    const scheduled = starts != null && starts.isAfter(now);
    const expired = ends != null && !ends.isAfter(now);
    const live = announcement.active && !scheduled && !expired;
    let statusLabel = 'Inactive';
    let statusColor: 'default' | 'success' | 'warning' | 'error' = 'default';
    if (!announcement.active) {
      statusLabel = 'Inactive';
    } else if (scheduled) {
      statusLabel = 'Scheduled';
      statusColor = 'warning';
    } else if (expired) {
      statusLabel = 'Expired';
      statusColor = 'default';
    } else {
      statusLabel = 'Live';
      statusColor = 'success';
    }
    return { scheduled, expired, live, statusLabel, statusColor };
  };

  const visibleAnnouncements = useMemo(() => {
    if (showAnnouncementHistory) {
      return announcements;
    }
    return announcements.filter((announcement) => {
      const { live, scheduled } = getAnnouncementStatus(announcement);
      return live || scheduled;
    });
  }, [announcements, showAnnouncementHistory]);

  const handleFeatureFlagToggle = async (key: string, enabled: boolean) => {
    setError('');
    setSuccess('');
    setFeatureFlagsSavingKey(key);
    try {
      await api.put(`/admin/feature-flags/${encodeURIComponent(key)}`, { enabled });
      await fetchFeatureFlags();
      await refreshPublicFeatureFlags();
      setSuccess('Feature flags updated.');
    } catch (e: unknown) {
      const ax = e as { response?: { data?: unknown } };
      const data = ax?.response?.data;
      setError(formatApiErrorMessage(data, 'Failed to update feature flag.'));
    } finally {
      setFeatureFlagsSavingKey(null);
    }
  };

  const fetchTiers = async () => {
    try {
      const response = await api.get<TierSummary[]>('/admin/tiers');
      setTiers(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('Failed to fetch tiers:', err);
      setTiers([]);
    }
  };

  const fetchTierFlags = async (tierId: number) => {
    try {
      const response = await api.get<TierFeatureFlagRow[]>(`/admin/tiers/${tierId}/feature-flags`);
      setTierFlags(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('Failed to fetch tier feature flags:', err);
      setTierFlags([]);
    }
  };

  const fetchTierUsers = async (tierId: number) => {
    try {
      const response = await api.get<string[]>(`/admin/tiers/${tierId}/users`);
      setTierUsers(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('Failed to fetch tier users:', err);
      setTierUsers([]);
    }
  };

  const fetchTierLimits = async (tierId: number) => {
    try {
      const response = await api.get<TierLimitRow[]>(`/admin/tiers/${tierId}/limits`);
      const rows = Array.isArray(response.data) ? response.data : [];
      setTierLimits(rows);
      setTierLimitDrafts(rows.reduce<Record<string, string>>((acc, r) => {
        acc[r.key] = String(r.value);
        return acc;
      }, {}));
    } catch (err) {
      console.error('Failed to fetch tier limits:', err);
      setTierLimits([]);
      setTierLimitDrafts({});
    }
  };

  const handleTierLimitSave = async (tierId: number, key: string) => {
    const draft = tierLimitDrafts[key];
    const value = Number(draft);
    if (draft === undefined || draft === '' || Number.isNaN(value) || value < 0) {
      setError('Limit value must be a number ≥ 0.');
      return;
    }
    setError('');
    setSuccess('');
    setTierLimitSavingKey(key);
    try {
      await api.put(`/admin/tiers/${tierId}/limits/${encodeURIComponent(key)}`, {
        value: Math.trunc(value),
      });
      await fetchTierLimits(tierId);
      setSuccess('Tier limit updated.');
    } catch (e: unknown) {
      const ax = e as { response?: { data?: unknown } };
      setError(formatApiErrorMessage(ax?.response?.data, 'Failed to update tier limit.'));
    } finally {
      setTierLimitSavingKey(null);
    }
  };

  const fetchLimitDefaults = async () => {
    try {
      const response = await api.get<LimitDefaultRow[]>('/admin/limit-defaults');
      const rows = Array.isArray(response.data) ? response.data : [];
      setLimitDefaultRows(rows);
      setLimitDefaultDrafts(rows.reduce<Record<string, string>>((acc, r) => {
        acc[r.key] = String(r.value);
        return acc;
      }, {}));
    } catch (err) {
      console.error('Failed to fetch limit defaults:', err);
      setLimitDefaultRows([]);
      setLimitDefaultDrafts({});
    }
  };

  const handleLimitDefaultSave = async (key: string) => {
    const draft = limitDefaultDrafts[key];
    const value = Number(draft);
    if (draft === undefined || draft === '' || Number.isNaN(value) || value < 0) {
      setError('Limit value must be a number ≥ 0.');
      return;
    }
    setError('');
    setSuccess('');
    setLimitDefaultSavingKey(key);
    try {
      await api.put(`/admin/limit-defaults/${encodeURIComponent(key)}`, {
        value: Math.trunc(value),
      });
      await fetchLimitDefaults();
      setSuccess('Global limit default updated.');
    } catch (e: unknown) {
      const ax = e as { response?: { data?: unknown } };
      setError(formatApiErrorMessage(ax?.response?.data, 'Failed to update global limit default.'));
    } finally {
      setLimitDefaultSavingKey(null);
    }
  };

  const handleCreateTier = async () => {
    if (!newTierName.trim()) return;
    setCreatingTier(true);
    setError('');
    setSuccess('');
    try {
      const response = await api.post<{ id: number }>('/admin/tiers', {
        name: newTierName.trim(),
        description: newTierDescription.trim() || null,
      });
      setNewTierName('');
      setNewTierDescription('');
      await fetchTiers();
      const createdId = response.data?.id;
      if (typeof createdId === 'number') setSelectedTierId(createdId);
      setSuccess('Tier created.');
    } catch (e: unknown) {
      const ax = e as { response?: { data?: unknown } };
      setError(formatApiErrorMessage(ax?.response?.data, 'Failed to create tier.'));
    } finally {
      setCreatingTier(false);
    }
  };

  const handleDeleteTier = async (tier: TierSummary) => {
    if (tier.userCount > 0) return;
    if (!window.confirm(`Delete tier "${tier.name}"? This cannot be undone.`)) return;
    setError('');
    setSuccess('');
    try {
      await api.delete(`/admin/tiers/${tier.id}`);
      if (selectedTierId === tier.id) setSelectedTierId(null);
      await fetchTiers();
      setSuccess('Tier deleted.');
    } catch (e: unknown) {
      const ax = e as { response?: { data?: unknown } };
      setError(formatApiErrorMessage(ax?.response?.data, 'Failed to delete tier.'));
    }
  };

  const handleTierFlagChange = async (
    tierId: number,
    key: string,
    mode: 'inherit' | 'on' | 'off',
  ) => {
    setError('');
    setSuccess('');
    setTierFlagSavingKey(key);
    try {
      const enabled = mode === 'inherit' ? null : mode === 'on';
      await api.put(`/admin/tiers/${tierId}/feature-flags/${encodeURIComponent(key)}`, { enabled });
      await fetchTierFlags(tierId);
      await refreshPublicFeatureFlags();
      setSuccess('Tier feature flag updated.');
    } catch (e: unknown) {
      const ax = e as { response?: { data?: unknown } };
      setError(formatApiErrorMessage(ax?.response?.data, 'Failed to update tier feature flag.'));
    } finally {
      setTierFlagSavingKey(null);
    }
  };

  const handleAssignUserToTier = async () => {
    if (assignUserId === '') return;
    setAssigningTier(true);
    setError('');
    setSuccess('');
    try {
      await api.post('/admin/tiers/assign', {
        userId: assignUserId,
        profileId: assignTierId === '' ? null : assignTierId,
      });
      await fetchTiers();
      if (selectedTierId != null) await fetchTierUsers(selectedTierId);
      await refreshPublicFeatureFlags();
      setSuccess('User tier updated.');
    } catch (e: unknown) {
      const ax = e as { response?: { data?: unknown } };
      setError(formatApiErrorMessage(ax?.response?.data, 'Failed to assign user to tier.'));
    } finally {
      setAssigningTier(false);
    }
  };

  const runSanityChecks = async () => {
    setLoadingSanity(true);
    setError('');
    try {
      const response = await api.post('/admin/sanity-checks/run');
      setSanityReport(response.data);
      if (!response.data?.results?.length) {
        setSuccess('Sanity checks completed — no issues found.');
      } else {
        setSuccess(`Sanity checks completed — ${response.data.results.length} issue(s) found.`);
      }
    } catch (err: any) {
      setSanityReport(null);
      setError(formatApiErrorMessage(err?.response?.data, 'Sanity check run failed.'));
    } finally {
      setLoadingSanity(false);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchStats();
    fetchOnlineUsers();
    fetchSystemHealth();
    fetchOpenDocuments();
    fetchSanityReport();
    fetchFeatureFlags();
    const interval = setInterval(() => {
      fetchStats();
      fetchUsers();
      fetchOnlineUsers();
      fetchSystemHealth();
      fetchOpenDocuments();
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  // Online users poll every 30s; refresh account list when opening Users / Administrators tabs
  useEffect(() => {
    if (usersSubTab === 1 || usersSubTab === 2) {
      fetchUsers();
    }
  }, [usersSubTab]);

  const handleSetExpiration = async () => {
    if (!editUser) return;
    setError(''); setSuccess('');
    try {
      await api.post('/admin/set-expiration', null, {
        params: { 
          username: editUser.username, 
          expirationDate: newExpiration || null 
        }
      });
      setSuccess('Expiration updated.');
      setEditUser(null);
      setNewExpiration('');
      fetchUsers();
    } catch (err: any) {
      setError(formatApiErrorMessage(err?.response?.data, 'Failed to update expiration.'));
    }
  };

  const handleToggleExpiration = async (user: User) => {
    setError(''); setSuccess('');
    try {
      await api.post(`/admin/user/${user.username}/toggle-expiration`);
      setSuccess('Account expiration status updated.');
      fetchUsers();
    } catch (err: any) {
      setError(formatApiErrorMessage(err?.response?.data, 'Failed to update account expiration status.'));
    }
  };

  const handleDeleteUser = async () => {
    if (!deleteUser) return;
    setError(''); setSuccess('');
    try {
      await api.delete(`/admin/user/${deleteUser.username}`);
      setSuccess('User deleted.');
      setDeleteUser(null);
      fetchUsers();
    } catch (err: any) {
      setError(formatApiErrorMessage(err?.response?.data, 'Failed to delete user.'));
    }
  };

  const handleResetPassword = async () => {
    if (!resetPasswordUser) return;
    setError(''); setSuccess('');
    try {
      await api.post(`/admin/user/${resetPasswordUser.username}/reset-password`);
      setSuccess(`Password reset email sent to ${resetPasswordUser.username}`);
      setResetPasswordUser(null);
    } catch (err: any) {
      setError(formatApiErrorMessage(err?.response?.data, 'Failed to send password reset email.'));
    }
  };

  const isCurrentUser = (user: User) => {
    return currentUser && user.username === currentUser.username;
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, user: User) => {
    setSelectedUser(user);
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedUser(null);
  };

  const fetchUserDetails = async (username: string) => {
    setLoadingDetails(true);
    setDetailsError(null);
    try {
      const response = await api.get(`/admin/user/${username}/details`);
      console.log('User details response:', response.data);
      setViewDetailsUser(response.data);
    } catch (err: any) {
      console.error('Error fetching user details:', err);
      const errorMessage = err?.response?.data?.message || 'Failed to fetch user details.';
      setDetailsError(errorMessage);
      setViewDetailsUser(null);
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleImpersonate = async (user: User, documentName?: string) => {
    setError('');
    setImpersonatingUserId(user.id);
    const viewKey = documentName ? `${user.id}:${documentName}` : null;
    if (viewKey) {
      setViewingDocumentKey(viewKey);
    }
    try {
      await startImpersonation(user.id, documentName ? { documentName } : undefined);
    } catch (err: any) {
      const message = err?.response?.data || err?.message || 'Failed to start impersonation.';
      setError(typeof message === 'string' ? message : 'Failed to start impersonation.');
      setImpersonatingUserId(null);
      setViewingDocumentKey(null);
    } finally {
      setImpersonatingUserId(null);
      setViewingDocumentKey(null);
    }
  };

  const handleViewDocumentReadOnly = (ownerUserId: number, documentName: string) => {
    void handleImpersonate({ id: ownerUserId } as User, documentName);
  };

  const handleAction = (action: 'expiration' | 'reset' | 'delete' | 'disable-expiration' | 'set-never-expire' | 'view-details' | 'make-admin' | 'remove-admin' | 'view-documents' | 'view-activity' | 'impersonate' | 'set-tier' | 'remove-tier') => {
    if (!selectedUser) return;
    
    switch (action) {
      case 'expiration':
        setEditUser(selectedUser);
        setNewExpiration(selectedUser.expirationDate ? formatServerLocal(selectedUser.expirationDate, 'YYYY-MM-DDTHH:mm', '') : '');
        break;
      case 'reset':
        setResetPasswordUser(selectedUser);
        break;
      case 'delete':
        setDeleteUser(selectedUser);
        break;
      case 'disable-expiration':
        handleDisableExpiration(selectedUser);
        break;
      case 'set-never-expire':
        handleSetNeverExpire(selectedUser);
        break;
      case 'view-details':
        fetchUserDetails(selectedUser.username);
        break;
      case 'make-admin':
        handleMakeAdmin(selectedUser.id);
        break;
      case 'remove-admin':
        handleRemoveAdmin(selectedUser.id);
        break;
      case 'view-documents':
        handleViewDocuments(selectedUser);
        break;
      case 'view-activity':
        setActivityUserSelection({ scope: 'user', user: selectedUser });
        setAdminTab(ADMIN_TAB_DOCUMENT_ACTIVITY);
        break;
      case 'impersonate':
        void handleImpersonate(selectedUser);
        break;
      case 'set-tier':
        void fetchTiers();
        setSetTierSelection(selectedUser.tierId ?? '');
        setSetTierUser(selectedUser);
        break;
      case 'remove-tier':
        void assignUserTier(selectedUser, null);
        break;
    }
    handleMenuClose();
  };

  const assignUserTier = async (user: User, profileId: number | null) => {
    setError('');
    setSuccess('');
    setSavingUserTier(true);
    try {
      await api.post('/admin/tiers/assign', { userId: user.id, profileId });
      setSuccess(
        profileId == null
          ? `Removed ${user.username} from their tier (now on global defaults).`
          : `Updated tier for ${user.username}.`,
      );
      await fetchUsers();
    } catch (e: unknown) {
      const ax = e as { response?: { data?: unknown } };
      setError(formatApiErrorMessage(ax?.response?.data, 'Failed to update user tier.'));
    } finally {
      setSavingUserTier(false);
    }
  };

  const handleSaveUserTier = async () => {
    if (!setTierUser) return;
    const profileId = setTierSelection === '' ? null : Number(setTierSelection);
    await assignUserTier(setTierUser, profileId);
    setSetTierUser(null);
  };

  const handleDisableExpiration = async (user: User) => {
    setError(''); setSuccess('');
    try {
      await api.post(`/admin/user/${user.username}/disable-expiration`);
      setSuccess('Account expiration enabled.');
      fetchUsers();
    } catch (err: any) {
      setError(formatApiErrorMessage(err?.response?.data, 'Failed to enable account expiration.'));
    }
  };

  const handleSetNeverExpire = async (user: User) => {
    setError(''); setSuccess('');
    try {
      await api.post(`/admin/user/${user.username}/set-never-expire`);
      setSuccess('Account set to never expire.');
      fetchUsers();
    } catch (err: any) {
      setError(formatApiErrorMessage(err?.response?.data, 'Failed to set account to never expire.'));
    }
  };

  const handleChangePassword = async () => {
    setError(''); setSuccess('');
    try {
      await api.post('/admin/change-password', null, {
        params: {
          currentPassword,
          newPassword
        }
      });
      setSuccess('Password changed successfully.');
      setChangePasswordOpen(false);
      setCurrentPassword('');
      setNewPassword('');
    } catch (err: any) {
      setError(formatApiErrorMessage(err?.response?.data, 'Failed to change password.'));
    }
  };

  const handleSort = (field: SortField) => {
    setSortConfig(prevConfig => ({
      field,
      order: prevConfig.field === field && prevConfig.order === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getSortedUsers = (users: User[]) => {
    return [...users].sort((a, b) => {
      const order = sortConfig.order === 'asc' ? 1 : -1;
      
      switch (sortConfig.field) {
        case 'username':
          return order * a.username.localeCompare(b.username);
        case 'creationDate':
          return compareServerDateTimes(a.creationDate, b.creationDate, order);
        case 'expirationDate':
          // Handle "Never" cases
          if (a.accountNeverExpires && !b.accountNeverExpires) return -1;
          if (!a.accountNeverExpires && b.accountNeverExpires) return 1;
          if (a.accountNeverExpires && b.accountNeverExpires) return 0;
          
          // Handle null expiration dates
          if (!a.expirationDate && !b.expirationDate) return 0;
          if (!a.expirationDate) return 1;
          if (!b.expirationDate) return -1;
          
          return compareServerDateTimes(a.expirationDate, b.expirationDate, order);
        case 'lastLoginAt':
          // Handle "Never" cases (null lastLoginAt)
          if (!a.lastLoginAt && !b.lastLoginAt) return 0;
          if (!a.lastLoginAt) return 1;
          if (!b.lastLoginAt) return -1;
          
          return compareServerDateTimes(a.lastLoginAt, b.lastLoginAt, order);
        default:
          return 0;
      }
    });
  };

  const isUserOnline = (username: string) => {
    return onlineUsers.some(user => user.username === username);
  };

  const renderSortLabel = (field: SortField, label: string) => (
    <TableSortLabel
      active={sortConfig.field === field}
      direction={sortConfig.field === field ? sortConfig.order : 'asc'}
      onClick={() => handleSort(field)}
    >
      {label}
    </TableSortLabel>
  );

  const handleOpenDocumentsSort = (field: OpenDocumentSortField) => {
    setOpenDocumentsSortConfig((prev) => ({
      field,
      order: prev.field === field && prev.order === 'asc' ? 'desc' : 'asc',
    }));
  };

  const renderOpenDocumentSortLabel = (field: OpenDocumentSortField, label: string) => (
    <TableSortLabel
      active={openDocumentsSortConfig.field === field}
      direction={openDocumentsSortConfig.field === field ? openDocumentsSortConfig.order : 'asc'}
      onClick={() => handleOpenDocumentsSort(field)}
    >
      {label}
    </TableSortLabel>
  );

  const handleUserDocumentsSort = (field: UserDocumentSortField) => {
    setUserDocumentsSortConfig((prev) => ({
      field,
      order: prev.field === field && prev.order === 'asc' ? 'desc' : 'asc',
    }));
  };

  const renderUserDocumentSortLabel = (field: UserDocumentSortField, label: string) => (
    <TableSortLabel
      active={userDocumentsSortConfig.field === field}
      direction={userDocumentsSortConfig.field === field ? userDocumentsSortConfig.order : 'asc'}
      onClick={() => handleUserDocumentsSort(field)}
    >
      {label}
    </TableSortLabel>
  );

  const compareOpenDocumentDates = (a: string | undefined, b: string | undefined, order: number) =>
    compareServerDateTimes(a, b, order);

  const sortedUserDocuments = useMemo(() => {
    const order = userDocumentsSortConfig.order === 'asc' ? 1 : -1;
    return [...userDocuments].sort((a, b) => {
      switch (userDocumentsSortConfig.field) {
        case 'name':
          return order * a.name.localeCompare(b.name);
        case 'revisionCount':
          return order * ((a.revisionCount ?? 0) - (b.revisionCount ?? 0));
        case 'latestRevisionAt':
          return compareOpenDocumentDates(a.latestRevisionAt, b.latestRevisionAt, order);
        case 'creationDate':
          return compareOpenDocumentDates(a.creationDate, b.creationDate, order);
        case 'updatedAt':
          return compareOpenDocumentDates(a.updatedAt, b.updatedAt, order);
        default:
          return 0;
      }
    });
  }, [userDocuments, userDocumentsSortConfig]);

  const filterUsers = (users: User[], searchTerm: string) => {
    if (!searchTerm) return users;
    const searchLower = searchTerm.toLowerCase();
    const trimmed = searchTerm.trim();
    return users.filter(user =>
      user.username.toLowerCase().includes(searchLower) ||
      (user.email?.toLowerCase().includes(searchLower) ?? false) ||
      String(user.id).includes(trimmed)
    );
  };

  const filterOnlineUsers = (list: OnlineUser[], term: string) => {
    if (!term.trim()) return list;
    const searchLower = term.toLowerCase();
    const trimmed = term.trim();
    return list.filter(
      (user) =>
        user.username.toLowerCase().includes(searchLower) ||
        (user.email?.toLowerCase().includes(searchLower) ?? false) ||
        String(user.id).includes(trimmed),
    );
  };

  const handleOptionalColumnsChange = (event: SelectChangeEvent<OptionalUserColumn[]>) => {
    const value = event.target.value;
    const selected = (typeof value === 'string' ? value.split(',') : value) as OptionalUserColumn[];
    const next = new Set(selected);
    localStorage.setItem(ADMIN_USER_COLUMNS_STORAGE_KEY, JSON.stringify(Array.from(next)));
    setVisibleOptionalColumns(next);
  };

  const optionalColumnsSelectValue = useMemo(
    () => Array.from(visibleOptionalColumns),
    [visibleOptionalColumns],
  );

  const renderOptionalColumnsValue = (selected: OptionalUserColumn[]) => {
    if (selected.length === 0) {
      return 'None';
    }
    return selected
      .map((key) => OPTIONAL_USER_COLUMNS.find((c) => c.key === key)?.label ?? key)
      .join(', ');
  };

  const renderOptionalUserHeaders = () => (
    <>
      {visibleOptionalColumns.has('email') && <TableCell>Email</TableCell>}
      {visibleOptionalColumns.has('name') && <TableCell>Name</TableCell>}
      {visibleOptionalColumns.has('documents') && <TableCell align="right">Documents</TableCell>}
      {visibleOptionalColumns.has('emailVerified') && <TableCell>Email verified</TableCell>}
      {visibleOptionalColumns.has('enabled') && <TableCell>Account</TableCell>}
      {visibleOptionalColumns.has('tier') && <TableCell>Tier</TableCell>}
    </>
  );

  const renderOptionalUserCells = (user: User) => (
    <>
      {visibleOptionalColumns.has('email') && (
        <TableCell sx={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis' }} title={user.email}>
          {user.email || '—'}
        </TableCell>
      )}
      {visibleOptionalColumns.has('name') && (
        <TableCell>{formatUserDisplayName(user)}</TableCell>
      )}
      {visibleOptionalColumns.has('documents') && (
        <TableCell align="right">{user.documentCount ?? 0}</TableCell>
      )}
      {visibleOptionalColumns.has('emailVerified') && (
        <TableCell>
          <Chip
            size="small"
            label={user.emailVerified ? 'Yes' : 'No'}
            color={user.emailVerified ? 'success' : 'default'}
            variant="outlined"
          />
        </TableCell>
      )}
      {visibleOptionalColumns.has('enabled') && (
        <TableCell>
          <Chip
            size="small"
            label={user.enabled === false ? 'Disabled' : 'Active'}
            color={user.enabled === false ? 'error' : 'success'}
            variant="outlined"
          />
        </TableCell>
      )}
      {visibleOptionalColumns.has('tier') && (
        <TableCell>
          {user.tierName ? (
            <Chip size="small" label={user.tierName} variant="outlined" />
          ) : (
            <Typography variant="caption" color="text.secondary">Global</Typography>
          )}
        </TableCell>
      )}
    </>
  );

  const adminUsersList = useMemo(() => users.filter((u) => u.admin), [users]);
  const regularUsersList = useMemo(() => users.filter((u) => !u.admin), [users]);
  const filteredOnlineUsers = useMemo(
    () => filterOnlineUsers(onlineUsers, searchTerm),
    [onlineUsers, searchTerm],
  );

  const renderAccountUserTableHead = () => (
    <TableRow>
      <TableCell>User ID</TableCell>
      <TableCell>{renderSortLabel('username', 'Username')}</TableCell>
      {renderOptionalUserHeaders()}
      <TableCell>Provider</TableCell>
      <TableCell>{renderSortLabel('creationDate', 'Created')}</TableCell>
      <TableCell>{renderSortLabel('expirationDate', 'Expiration')}</TableCell>
      <TableCell>{renderSortLabel('lastLoginAt', 'Last Login')}</TableCell>
      <TableCell>Online</TableCell>
      <TableCell align="right">Actions</TableCell>
    </TableRow>
  );

  const filteredOpenDocuments = useMemo(() => {
    const q = openDocumentsSearch.trim().toLowerCase();
    let list = openDocuments;
    if (q) {
      list = list.filter((doc) => {
        const idStr = String(doc.id ?? '');
        return (
          doc.name.toLowerCase().includes(q) ||
          doc.user.username.toLowerCase().includes(q) ||
          (doc.user.email?.toLowerCase().includes(q) ?? false) ||
          idStr.toLowerCase().includes(q)
        );
      });
    }
    const order = openDocumentsSortConfig.order === 'asc' ? 1 : -1;
    return [...list].sort((a, b) => {
      switch (openDocumentsSortConfig.field) {
        case 'name':
          return order * a.name.localeCompare(b.name);
        case 'user':
          return order * a.user.username.localeCompare(b.user.username);
        case 'creationDate':
          return compareOpenDocumentDates(a.creationDate, b.creationDate, order);
        case 'updatedAt':
          return compareOpenDocumentDates(a.updatedAt, b.updatedAt, order);
        default:
          return 0;
      }
    });
  }, [openDocuments, openDocumentsSearch, openDocumentsSortConfig]);

  const getProviderData = () => {
    if (!stats?.usersByProvider) return [];
    const data = [
      { name: 'Local', value: stats.usersByProvider.LOCAL || 0 },
      { name: 'Google', value: stats.usersByProvider.GOOGLE || 0 },
    ];
    return data;
  };

  const handleMakeAdmin = async (userId: number) => {
    setLoading(true);
    try {
      await api.post(`/admin/users/${userId}/admin`);
      // Refresh the users list
      await fetchUsers();
      // Show success message
      setSnackbar({
        open: true,
        message: 'User has been made an admin',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error making user admin:', error);
      // Don't show error message if the operation succeeded
      await fetchUsers();
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveAdmin = async (userId: number) => {
    setLoading(true);
    try {
      await api.post(`/admin/users/${userId}/admin`);
      // Refresh the users list
      await fetchUsers();
      // Show success message
      setSnackbar({
        open: true,
        message: 'Admin privileges have been removed',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error removing admin privileges:', error);
      // Don't show error message if the operation succeeded
      await fetchUsers();
    } finally {
      setLoading(false);
    }
  };

  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  };

  const getHealthColor = (value: number, thresholds: { warning: number; error: number }) => {
    if (value >= thresholds.error) return 'error';
    if (value >= thresholds.warning) return 'warning';
    return 'success';
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'healthy': return 'success';
      case 'degraded': return 'warning';
      case 'unhealthy': return 'error';
      default: return 'error';
    }
  };

  const handleCloseDocument = async (id: string) => {
    try {
      await api.post(`/admin/open-documents/${String(id)}/close`);
      setSnackbar({
        open: true,
        message: 'Document closed successfully',
        severity: 'success'
      });
      fetchOpenDocuments(); // Refresh the list
    } catch (err) {
      setSnackbar({
        open: true,
        message: 'Failed to close document',
        severity: 'error'
      });
    }
  };

  const confirmReloadOpenDocumentCache = async () => {
    if (!reloadCacheDoc) return;
    setReloadCacheLoading(true);
    try {
      await api.post(`/admin/open-documents/${String(reloadCacheDoc.id)}/reload-from-database`);
      setSnackbar({
        open: true,
        message: 'Document cache reloaded from database',
        severity: 'success'
      });
      setReloadCacheDoc(null);
      fetchOpenDocuments();
    } catch (err: any) {
      const data = err.response?.data;
      const msg = typeof data === 'string' ? data : (data?.message ?? 'Failed to reload document cache');
      const status = err.response?.status;
      setSnackbar({
        open: true,
        message: status === 409 ? `${msg} Try again in a few seconds.` : msg,
        severity: 'error'
      });
    } finally {
      setReloadCacheLoading(false);
    }
  };

  const confirmPersistOpenDocumentToDatabase = async () => {
    if (!saveToDbDoc) return;
    setSaveToDbLoading(true);
    try {
      const response = await api.post(
        `/admin/open-documents/${String(saveToDbDoc.id)}/persist-to-database`
      );
      const msg =
        response.data?.message ??
        (response.data?.persisted ? 'Saved to database.' : 'Already up to date.');
      setSnackbar({
        open: true,
        message: msg,
        severity: 'success'
      });
      setSaveToDbDoc(null);
      fetchOpenDocuments();
    } catch (err: any) {
      const data = err.response?.data;
      const msg =
        typeof data?.message === 'string'
          ? data.message
          : typeof data === 'string'
            ? data
            : 'Failed to save document to database';
      const status = err.response?.status;
      setSnackbar({
        open: true,
        message: status === 409 ? `${msg} Try again in a few seconds.` : msg,
        severity: 'error'
      });
    } finally {
      setSaveToDbLoading(false);
    }
  };

  const handleViewDocuments = async (user: User) => {
    setViewDocumentsUser(user);
    setLoadingDocuments(true);
    try {
      const response = await api.get(`/admin/users/${user.id}/documents`);
      setUserDocuments(response.data);
    } catch (err) {
      setSnackbar({
        open: true,
        message: 'Failed to fetch user documents',
        severity: 'error'
      });
    } finally {
      setLoadingDocuments(false);
    }
  };

  const handleViewRevisions = async (doc: UserDocument) => {
    if (!viewDocumentsUser) return;
    setViewRevisionsDocument(doc);
    setLoadingRevisions(true);
    setDocumentRevisions([]);
    try {
      const response = await api.get(`/admin/users/${viewDocumentsUser.id}/documents/${doc.id}/revisions`);
      setDocumentRevisions(response.data || []);
    } catch (err) {
      setSnackbar({
        open: true,
        message: 'Failed to fetch document revisions',
        severity: 'error'
      });
      setDocumentRevisions([]);
    } finally {
      setLoadingRevisions(false);
    }
  };

  const handleCopyRevisionToAdmin = async (revisionId: number) => {
    if (!viewDocumentsUser || !viewRevisionsDocument) return;
    setCopyingRevisionId(revisionId);
    try {
      const response = await api.post(
        `/admin/users/${viewDocumentsUser.id}/documents/${viewRevisionsDocument.id}/revisions/${revisionId}/copy-to-admin`
      );
      const created = response.data;
      setSnackbar({
        open: true,
        message: `Revision copied to your library as "${created?.name || 'new document'}". Open it from the main app document list.`,
        severity: 'success'
      });
    } catch (err: any) {
      const data = err.response?.data;
      const msg =
        typeof data?.message === 'string'
          ? data.message
          : typeof data === 'string'
            ? data
            : 'Failed to copy revision to your library';
      setSnackbar({
        open: true,
        message: msg,
        severity: 'error'
      });
    } finally {
      setCopyingRevisionId(null);
    }
  };

  const handleRestoreRevisionToUser = async (revisionId: number) => {
    if (!viewDocumentsUser || !viewRevisionsDocument) return;
    setRestoringRevisionId(revisionId);
    try {
      const response = await api.post(
        `/admin/users/${viewDocumentsUser.id}/documents/${viewRevisionsDocument.id}/revisions/${revisionId}/restore-as-copy`
      );
      const created = response.data;
      setSnackbar({
        open: true,
        message: `Recovered revision into ${viewDocumentsUser.username}'s library as "${created?.name || 'new document'}".`,
        severity: 'success'
      });
      setRestoreRevisionConfirm(null);
      await handleViewDocuments(viewDocumentsUser);
    } catch (err: any) {
      const data = err.response?.data;
      const msg =
        typeof data?.message === 'string'
          ? data.message
          : typeof data === 'string'
            ? data
            : 'Failed to recover revision for user';
      setSnackbar({
        open: true,
        message: msg,
        severity: 'error'
      });
    } finally {
      setRestoringRevisionId(null);
    }
  };

  const handleCopyUserDocumentToAdmin = async (doc: UserDocument) => {
    if (!viewDocumentsUser) return;
    setCopyingDocumentId(String(doc.id));
    try {
      const response = await api.post(
        `/admin/users/${viewDocumentsUser.id}/documents/${doc.id}/copy-to-admin`
      );
      const created = response.data;
      const persistNote =
        created?.wasOpenDocument && created?.persistedDirtyChangesToDatabase
          ? ' The user’s open session had unsaved edits; those were saved to the database before copying.'
          : created?.wasOpenDocument
            ? ' The document was open on the server; its cache already matched the database.'
            : '';
      setSnackbar({
        open: true,
        message: `Document copied to your library as "${created?.name || 'new document'}". Open it from the main app document list.${persistNote}`,
        severity: 'success'
      });
    } catch (err: any) {
      const data = err.response?.data;
      const status = err.response?.status;
      const msg =
        typeof data?.message === 'string'
          ? data.message
          : typeof data === 'string'
            ? data
            : 'Failed to copy document to your library';
      setSnackbar({
        open: true,
        message: status === 409 ? `${msg} Try again in a few seconds.` : msg,
        severity: 'error'
      });
    } finally {
      setCopyingDocumentId(null);
    }
  };

  const handleDeleteDocument = async () => {
    if (!deleteDocument || !viewDocumentsUser) return;
    
    try {
      await api.delete(`/admin/users/${viewDocumentsUser.id}/documents/${deleteDocument.name}`);
      setSnackbar({
        open: true,
        message: 'Document deleted successfully',
        severity: 'success'
      });
      // Refresh the documents list
      handleViewDocuments(viewDocumentsUser);
    } catch (err) {
      setSnackbar({
        open: true,
        message: 'Failed to delete document',
        severity: 'error'
      });
    } finally {
      setDeleteDocument(null);
    }
  };

  const handleOpenDocumentActions = (event: React.MouseEvent<HTMLElement>, doc: UserDocument) => {
    setDocumentActionsAnchorEl(event.currentTarget);
    setDocumentActionsDoc(doc);
  };

  const handleCloseDocumentActions = () => {
    setDocumentActionsAnchorEl(null);
    setDocumentActionsDoc(null);
  };

  const getExpirationStatus = (expirationDate: string | null, accountNeverExpires: boolean) => {
    if (accountNeverExpires) {
      return { 
        text: 'Never Expires', 
        color: 'success',
        icon: <LockIcon fontSize="small" />
      };
    }
    
    if (!expirationDate) {
      return { 
        text: 'No Expiration', 
        color: 'success',
        icon: <LockIcon fontSize="small" />
      };
    }

    const expiration = serverDateTimeDayjs(expirationDate);
    const now = dayjs();
    
    if (expiration.isBefore(now)) {
      return { 
        text: 'Expired', 
        color: 'error',
        icon: <ErrorIcon fontSize="small" />
      };
    }
    
    const daysUntilExpiration = expiration.diff(now, 'day');
    if (daysUntilExpiration <= 7) {
      const hoursUntilExpiration = expiration.diff(now, 'hour');
      if (hoursUntilExpiration < 24) {
        return { 
          text: `${hoursUntilExpiration} hours remaining`, 
          color: 'error',
          icon: <ErrorIcon fontSize="small" />
        };
      }
      return { 
        text: `${daysUntilExpiration} days remaining`, 
        color: 'warning',
        icon: <AccessTimeIcon fontSize="small" />
      };
    }
    
    return { 
      text: expiration.format('MMM D, YYYY'), 
      color: 'success',
      icon: <CheckCircleIcon fontSize="small" />
    };
  };

  const handleRepair = async (checkName: string, repairId: string) => {
    setRepairingId(repairId);
    setError('');
    setSuccess('');
    try {
      await api.post('/admin/sanity-checks/repair', { checkName, repairId });
      setLoadingSanity(true);
      const response = await api.post('/admin/sanity-checks/run');
      setSanityReport(response.data);
      setSuccess('Repair applied. Sanity checks re-run.');
    } catch (err) {
      setError(formatApiErrorMessage((err as any)?.response?.data, 'Repair failed.'));
    } finally {
      setRepairingId(null);
      setLoadingSanity(false);
    }
  };

  const handleRepairAll = async () => {
    if (!sanityReport?.results) return;
    const repairable = sanityReport.results.filter((r: any) => r.repairable);
    if (repairable.length === 0) return;

    setRepairingAll(true);
    setError('');
    setSuccess('');
    setRepairAllProgress({ completed: 0, total: repairable.length, phase: 'repairing' });

    let succeeded = 0;
    let failed = 0;

    try {
      for (let i = 0; i < repairable.length; i++) {
        const row = repairable[i];
        setRepairAllProgress({
          completed: i,
          total: repairable.length,
          currentCheck: row.checkName,
          phase: 'repairing',
        });
        try {
          await api.post('/admin/sanity-checks/repair', {
            checkName: row.checkName,
            repairId: row.repairId,
          });
          succeeded++;
        } catch {
          failed++;
        }
        setRepairAllProgress({
          completed: i + 1,
          total: repairable.length,
          currentCheck: row.checkName,
          phase: 'repairing',
        });
      }

      setRepairAllProgress({
        completed: repairable.length,
        total: repairable.length,
        phase: 'refreshing',
      });
      setLoadingSanity(true);
      const response = await api.post('/admin/sanity-checks/run');
      setSanityReport(response.data);

      if (failed > 0) {
        setSuccess(
          `Repairs finished: ${succeeded} succeeded, ${failed} failed. Sanity checks re-run.`,
        );
      } else {
        setSuccess(`Repaired ${succeeded} issue(s). Sanity checks re-run.`);
      }
    } catch (err) {
      setError(
        formatApiErrorMessage(
          (err as any)?.response?.data,
          'Failed to refresh sanity checks after repair.',
        ),
      );
    } finally {
      setRepairingAll(false);
      setRepairAllProgress(null);
      setLoadingSanity(false);
    }
  };

  return (
    <Box
      sx={{
        bgcolor: 'background.default',
        minHeight: '100%',
        py: { xs: 1.5, sm: 2 },
        px: { xs: 1, sm: 2 },
      }}
    >
    <Container maxWidth={false} sx={{ 
      mt: 0, 
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden'
    }}>
      <Paper sx={{ 
        p: 3, 
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        minHeight: 0,
        borderRadius: 2,
        border: (theme) => `1px solid ${theme.palette.divider}`,
        boxShadow: (theme) => theme.shadows[1],
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4">Admin Console</Typography>
          <Button 
            variant="contained" 
            onClick={() => setChangePasswordOpen(true)}
          >
            Change Password
      </Button>
        </Box>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
        
        <Tabs
          value={adminTab}
          onChange={(_, v) => setAdminTab(v as number)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          aria-label="Admin console sections"
          sx={{ borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}
        >
          <Tab label="Statistics" />
          <Tab label="System health" />
          <Tab label="Global defaults" />
          <Tab label="Users" />
          <Tab label="Open documents" />
          <Tab label="Document activity" />
          <Tab label="Audit logs" />
          <Tab label="Announcements" />
          <Tab
            label={
              sanityReport?.results?.length
                ? `Sanity checks (${sanityReport.results.length})`
                : 'Sanity checks'
            }
          />
          <Tab label="Tiers" />
        </Tabs>
        <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', pt: 2, display: 'flex', flexDirection: 'column' }}>
          {adminTab === 0 && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <PeopleIcon color="primary" sx={{ mr: 1 }} />
                      <Typography variant="h6">Total Users</Typography>
                    </Box>
                    <Typography variant="h4">{stats?.totalUsers || 0}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <PersonIcon color="success" sx={{ mr: 1 }} />
                      <Typography variant="h6">Active Users</Typography>
                    </Box>
                    <Typography variant="h4">{stats?.activeUsers || 0}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <AccessTimeIcon color="error" sx={{ mr: 1 }} />
                      <Typography variant="h6">Expired Users</Typography>
                    </Box>
                    <Typography variant="h4">{stats?.expiredUsers || 0}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <AdminPanelSettingsIcon color="info" sx={{ mr: 1 }} />
                      <Typography variant="h6">Administrators</Typography>
                    </Box>
                    <Typography variant="h4">{stats?.adminUsers || 0}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <PersonIcon color="warning" sx={{ mr: 1 }} />
                      <Typography variant="h6">Online Users</Typography>
                    </Box>
                    <Typography variant="h4">{onlineUsers.length}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <StorageIcon color="secondary" sx={{ mr: 1 }} />
                      <Typography variant="h6">Redis Keys</Typography>
                    </Box>
                    <Typography variant="h4">{stats?.redisDbSize || 0}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <MemoryIcon color={stats?.redisMemoryUsage && stats.redisMemoryUsage > 1024 * 1024 * 1024 ? "error" : "secondary"} sx={{ mr: 1 }} />
                      <Typography variant="h6">Redis Memory</Typography>
                    </Box>
                    <Typography variant="h4" color={stats?.redisMemoryUsage && stats.redisMemoryUsage > 1024 * 1024 * 1024 ? "error" : "text.primary"}>
                      {stats?.redisMemoryUsage ? formatBytes(stats.redisMemoryUsage) : '0 B'}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <DescriptionIcon color="secondary" sx={{ mr: 1 }} />
                      <Typography variant="h6">Documents</Typography>
                    </Box>
                    <Typography variant="h4">{stats?.totalDocuments || 0}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <StorageOutlinedIcon color="secondary" sx={{ mr: 1 }} />
                      <Typography variant="h6">Revisions</Typography>
                    </Box>
                    <Typography variant="h4">{stats?.totalRevisions || 0}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {stats?.totalRevisionBytes ? formatBytes(stats.totalRevisionBytes) : '0 B'}
                    </Typography>
                    {stats?.postgresDatabaseBytes != null && stats.postgresDatabaseBytes > 0 && (() => {
                      const revPct = clampStoragePercent(stats.totalRevisionBytes ?? 0, stats.postgresDatabaseBytes);
                      return (
                        <Box sx={{ mt: 1.5 }}>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Revision payload vs PostgreSQL database
                          </Typography>
                          <LinearProgress
                            variant="determinate"
                            value={revPct}
                            color={revPct >= 85 ? 'error' : revPct >= 50 ? 'warning' : 'primary'}
                            sx={{ mt: 0.5, height: 6, borderRadius: 1 }}
                          />
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                            {formatBytes(stats.totalRevisionBytes ?? 0)} revision data · {formatBytes(stats.postgresDatabaseBytes)} database
                          </Typography>
                          {stats.postgresDocumentRevisionsRelationBytes != null &&
                            stats.postgresDocumentRevisionsRelationBytes > 0 && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              Revisions table (incl. TOAST / indexes): {formatBytes(stats.postgresDocumentRevisionsRelationBytes)}
                            </Typography>
                          )}
                        </Box>
                      );
                    })()}
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <PermMediaOutlinedIcon color="secondary" sx={{ mr: 1 }} />
                      <Typography variant="h6">Content media</Typography>
                    </Box>
                    <Typography variant="h4">{stats?.totalMediaFiles ?? 0}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {stats?.totalMediaBytes != null ? formatBytes(stats.totalMediaBytes) : '0 B'}
                    </Typography>
                    {(stats?.mediaStagingBytes != null || stats?.mediaByContentBytes != null) && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                        Staging {formatBytes(stats.mediaStagingBytes ?? 0)} · Stored {formatBytes(stats.mediaByContentBytes ?? 0)}
                      </Typography>
                    )}
                    {stats?.mediaVolumeTotalBytes != null && stats.mediaVolumeTotalBytes > 0 && (() => {
                      const volUsed =
                        stats.mediaVolumeUsedBytes ??
                        Math.max(0, stats.mediaVolumeTotalBytes - (stats.mediaVolumeUsableBytes ?? 0));
                      const volPct = clampStoragePercent(volUsed, stats.mediaVolumeTotalBytes);
                      return (
                        <Box sx={{ mt: 1.5 }}>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Disk volume (media directory)
                          </Typography>
                          <LinearProgress
                            variant="determinate"
                            value={volPct}
                            color={volPct >= 90 ? 'error' : volPct >= 75 ? 'warning' : 'primary'}
                            sx={{ mt: 0.5, height: 6, borderRadius: 1 }}
                          />
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                            {formatBytes(volUsed)} used of {formatBytes(stats.mediaVolumeTotalBytes)} · app media{' '}
                            {formatBytes(stats.totalMediaBytes ?? 0)}
                          </Typography>
                        </Box>
                      );
                    })()}
                    <Button
                      size="small"
                      variant="outlined"
                      sx={{ mt: 1.5 }}
                      onClick={() => setStagingPurgeDialogOpen(true)}
                      disabled={stagingPurgeLoading}
                    >
                      Purge stale staging…
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={3}>
                <Card sx={{ height: 300 }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2 }}>Users by Provider</Typography>
                    <Box sx={{ height: 200, width: '100%' }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={getProviderData()}
                            cx="50%"
                            cy="50%"
                            innerRadius={30}
                            outerRadius={80}
                            fill="#8884d8"
                            dataKey="value"
                            nameKey="name"
                            startAngle={0}
                            endAngle={360}
                            paddingAngle={2}
                          >
                            {getProviderData().map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip 
                            formatter={(value, name) => [`${value} Users`, name]}
                            contentStyle={{ 
                              backgroundColor: 'rgba(255, 255, 255, 0.9)',
                              border: '1px solid #ccc',
                              borderRadius: '4px',
                              padding: '8px'
                            }}
                          />
                          <Legend
                            verticalAlign="bottom"
                            align="center"
                            formatter={(value) => `${value} Users`}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={3}>
                <Card sx={{ height: 300 }}>
                  <CardContent>
                    <Box display="flex" alignItems="center" mb={2}>
                      <AddIcon sx={{ mr: 1 }} />
                      <Typography variant="h6">New Documents</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Box>
                        <Typography variant="h4">{stats?.newDocuments24h || 0}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Last 24 Hours
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="h6">{stats?.newDocuments7d || 0}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Last 7 Days
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="h6">{stats?.newDocuments30d || 0}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Last 30 Days
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={3}>
                <Card sx={{ height: 300 }}>
                  <CardContent>
                    <Box display="flex" alignItems="center" mb={2}>
                      <PersonAddIcon sx={{ mr: 1 }} />
                      <Typography variant="h6">New Users</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Box>
                        <Typography variant="h4">{stats?.newUsers24h || 0}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Last 24 Hours
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="h6">{stats?.newUsers7d || 0}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Last 7 Days
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="h6">{stats?.newUsers30d || 0}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Last 30 Days
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={3}>
                <Card sx={{ height: 300 }}>
                  <CardContent>
                    <Box display="flex" alignItems="center" mb={2}>
                      <AccessTimeIcon sx={{ mr: 1 }} />
                      <Typography variant="h6">Sessions</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Box>
                        <Typography variant="h4">{stats?.activeSessions || 0}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Active Sessions
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="h6">{Math.round(stats?.averageSessionDuration || 0)}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Avg. Session Duration (minutes)
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
          {adminTab === 1 && (
            <>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <MemoryIcon sx={{ mr: 1, color: systemHealth ? getHealthColor(systemHealth.cpuUsage, { warning: 60, error: 80 }) : 'primary' }} />
                      <Typography variant="h6">CPU Usage</Typography>
                    </Box>
                    <Typography variant="h4" sx={{ mb: 1, color: systemHealth ? getHealthColor(systemHealth.cpuUsage, { warning: 60, error: 80 }) : 'primary' }}>
                      {systemHealth ? `${Math.round(systemHealth.cpuUsage)}%` : 'N/A'}
                    </Typography>
                    <LinearProgress 
                      variant="determinate" 
                      value={systemHealth?.cpuUsage || 0} 
                      color={systemHealth ? getHealthColor(systemHealth.cpuUsage, { warning: 60, error: 80 }) as 'success' | 'warning' | 'error' : 'primary'}
                    />
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <MemoryIcon sx={{ mr: 1, color: systemHealth ? getHealthColor(systemHealth.memoryUsage, { warning: 60, error: 80 }) : 'primary' }} />
                      <Typography variant="h6">Memory Usage</Typography>
                    </Box>
                    <Typography variant="h4" sx={{ mb: 1, color: systemHealth ? getHealthColor(systemHealth.memoryUsage, { warning: 60, error: 80 }) : 'primary' }}>
                      {systemHealth ? `${Math.round(systemHealth.memoryUsage)}%` : 'N/A'}
                    </Typography>
                    <LinearProgress 
                      variant="determinate" 
                      value={systemHealth?.memoryUsage || 0} 
                      color={systemHealth ? getHealthColor(systemHealth.memoryUsage, { warning: 60, error: 80 }) as 'success' | 'warning' | 'error' : 'primary'}
                    />
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <MemoryIcon sx={{ mr: 1, color: systemHealth ? getHealthColor(systemHealth.jvmMemoryUsage, { warning: 60, error: 80 }) : 'primary' }} />
                      <Typography variant="h6">JVM Memory Usage</Typography>
                    </Box>
                    <Typography variant="h4" sx={{ mb: 1, color: systemHealth ? getHealthColor(systemHealth.jvmMemoryUsage, { warning: 60, error: 80 }) : 'primary' }}>
                      {systemHealth ? `${Math.round(systemHealth.jvmMemoryUsage)}%` : 'N/A'}
                    </Typography>
                    <LinearProgress 
                      variant="determinate" 
                      value={systemHealth?.jvmMemoryUsage || 0} 
                      color={systemHealth ? getHealthColor(systemHealth.jvmMemoryUsage, { warning: 60, error: 80 }) as 'success' | 'warning' | 'error' : 'primary'}
                    />
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <MemoryIcon sx={{ mr: 1, color: systemHealth ? getHealthColor(systemHealth.threadCount, { warning: 50, error: 100 }) : 'primary' }} />
                      <Typography variant="h6">Thread Count</Typography>
                    </Box>
                    <Typography variant="h4" sx={{ mb: 1, color: systemHealth ? getHealthColor(systemHealth.threadCount, { warning: 50, error: 100 }) : 'primary' }}>
                      {systemHealth ? systemHealth.threadCount : 'N/A'}
                    </Typography>
                    <LinearProgress 
                      variant="determinate" 
                      value={systemHealth ? (systemHealth.threadCount / 100) * 100 : 0} 
                      color={systemHealth ? getHealthColor(systemHealth.threadCount, { warning: 50, error: 100 }) as 'success' | 'warning' | 'error' : 'primary'}
                    />
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <StorageIcon sx={{ mr: 1, color: systemHealth ? getStatusColor(systemHealth.databaseStatus) : 'primary' }} />
                      <Typography variant="h6">Database Status</Typography>
                    </Box>
                    <Typography variant="h4">
                      {systemHealth?.databaseStatus ? (
                        <Chip 
                          label={systemHealth.databaseStatus.toUpperCase()}
                          color={
                            systemHealth.databaseStatus === 'healthy' ? 'success' :
                            systemHealth.databaseStatus === 'degraded' ? 'warning' : 'error'
                          }
                        />
                      ) : 'N/A'}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <StorageIcon sx={{ mr: 1, color: systemHealth ? getStatusColor(systemHealth.redisStatus) : 'primary' }} />
                      <Typography variant="h6">Redis Status</Typography>
                    </Box>
                    <Typography variant="h4">
                      {systemHealth?.redisStatus ? (
                        <Chip 
                          label={systemHealth.redisStatus.toUpperCase()}
                          color={
                            systemHealth.redisStatus === 'healthy' ? 'success' :
                            systemHealth.redisStatus === 'degraded' ? 'warning' : 'error'
                          }
                        />
                      ) : 'N/A'}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <StorageIcon sx={{ mr: 1, color: systemHealth ? getStatusColor(systemHealth.embeddingStatus) : 'primary' }} />
                      <Typography variant="h6" noWrap>Embedding Service</Typography>
                    </Box>
                    <Typography variant="h4">
                      {systemHealth?.embeddingStatus ? (
                        <Chip 
                          label={systemHealth.embeddingStatus.toUpperCase()}
                          color={
                            systemHealth.embeddingStatus === 'healthy' ? 'success' :
                            systemHealth.embeddingStatus === 'degraded' ? 'warning' : 'error'
                          }
                        />
                      ) : 'N/A'}
                    </Typography>
                    {systemHealth?.embeddingError && (
                      <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                        Error: {systemHealth.embeddingError}
                      </Typography>
                    )}
                    {systemHealth?.embeddingMessage && (
                      <Typography color="warning.main" variant="body2" sx={{ mt: 1 }}>
                        {systemHealth.embeddingMessage}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <SpeedIcon color="primary" sx={{ mr: 1 }} />
                      <Typography variant="h6">API Response Time</Typography>
                    </Box>
                    <Typography variant="h4">
                      {systemHealth?.apiResponseTime ? `${systemHealth.apiResponseTime.toFixed(0)}ms` : 'N/A'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Average response time
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
            <Card sx={{ mt: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                  <AutoAwesomeIcon color="primary" />
                  <Typography variant="h6">OpenAI — category naming (embedding service)</Typography>
                  <Chip
                    size="small"
                    label={formatOpenAiStatus(systemHealth?.openaiStatus)}
                    color={openAiStatusChipColor(systemHealth?.openaiStatus)}
                  />
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Tracks chat completions used to name categories during structure-from-files and similar flows.
                  Embeddings still run locally when quota is exceeded; names fall back to text-derived titles.
                  {systemHealth?.openaiApiKeyHint && (
                    <> API key on embedding service: <strong>{systemHealth.openaiApiKeyHint}</strong>.</>
                  )}
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    disabled={openaiProbing}
                    onClick={() => void runOpenAiProbe()}
                  >
                    {openaiProbing ? 'Testing…' : 'Test OpenAI connection'}
                  </Button>
                </Stack>
                {openaiProbeMessage && (
                  <Alert severity={openaiProbeMessage.severity} sx={{ mb: 2 }}>
                    {openaiProbeMessage.text}
                  </Alert>
                )}
                {(systemHealth?.openaiStatus === 'quota_exceeded' ||
                  systemHealth?.openaiStatus === 'rate_limited' ||
                  systemHealth?.openaiStatus === 'auth_error') && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {systemHealth.openaiStatus === 'quota_exceeded' && (
                      <>
                        OpenAI still reports insufficient quota for the API key on the embedding service (not the
                        Spring backend). Confirm billing for that key&apos;s project, check monthly spend limits, then
                        use Test OpenAI connection above or restart the embedding container.{' '}
                        <Typography
                          component="a"
                          href="https://platform.openai.com/account/billing"
                          target="_blank"
                          rel="noopener noreferrer"
                          variant="body2"
                        >
                          Open billing dashboard
                        </Typography>
                      </>
                    )}
                    {systemHealth.openaiStatus === 'rate_limited' &&
                      'OpenAI returned rate limits (HTTP 429). Requests are retried automatically; sustained limits may need a higher tier.'}
                    {systemHealth.openaiStatus === 'auth_error' &&
                      'OpenAI rejected the API key configured on the embedding service (OPENAI_API_KEY).'}
                  </Alert>
                )}
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="caption" color="text.secondary">
                      Model
                    </Typography>
                    <Typography variant="body2">{systemHealth?.openaiNamingModel ?? '—'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="caption" color="text.secondary">
                      Last success
                    </Typography>
                    <Typography variant="body2">
                      {formatHealthTimestamp(systemHealth?.openaiLastSuccessAt)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="caption" color="text.secondary">
                      Last error
                    </Typography>
                    <Typography variant="body2">
                      {formatHealthTimestamp(systemHealth?.openaiLastErrorAt)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="caption" color="text.secondary">
                      Errors (session)
                    </Typography>
                    <Typography variant="body2">
                      {systemHealth?.openaiTotalErrors != null
                        ? `${systemHealth.openaiTotalErrors} total`
                        : '—'}
                      {systemHealth?.openaiConsecutiveErrors != null &&
                      systemHealth.openaiConsecutiveErrors > 0
                        ? ` · ${systemHealth.openaiConsecutiveErrors} in a row`
                        : ''}
                    </Typography>
                  </Grid>
                  {systemHealth?.openaiLastErrorMessage && (
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">
                        Last error message
                        {systemHealth.openaiLastErrorCode
                          ? ` (${systemHealth.openaiLastErrorCode})`
                          : ''}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ mt: 0.5, fontFamily: 'monospace', fontSize: '0.8rem', wordBreak: 'break-word' }}
                      >
                        {systemHealth.openaiLastErrorMessage}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
            </>
          )}
          {adminTab === 2 && (
            <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              The system-wide baseline for all users. A user without a tier uses these values; tiers override them per
              segment (see the Tiers tab).
            </Typography>
            <Typography variant="h6" sx={{ mb: 1 }}>Feature flags</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Enable or disable product features for all signed-in users. The main UI picks up changes when you refresh
              or navigate; this page also refreshes the in-memory flag cache after each save.
            </Typography>
            <Stack spacing={1.5}>
              {featureFlagRows.map((row) => {
                const isMaster = row.key === 'AI_FEATURES_MASTER';
                const masterOff =
                  !featureFlagRows.find((r) => r.key === 'AI_FEATURES_MASTER')?.enabled;
                const switchChecked = row.aiFeature || isMaster ? row.storedEnabled : row.enabled;
                const switchDisabled = featureFlagsSavingKey !== null;
                return (
                  <Paper
                    key={row.key}
                    variant="outlined"
                    sx={{
                      p: 2,
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 2,
                      ...(isMaster ? { borderColor: 'primary.main', borderWidth: 2 } : {}),
                      ...(row.disabledByMaster ? { opacity: 0.85 } : {}),
                    }}
                  >
                    <Switch
                      checked={switchChecked}
                      onChange={(_, c) => void handleFeatureFlagToggle(row.key, c)}
                      disabled={switchDisabled}
                      inputProps={{ 'aria-label': row.label }}
                    />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="subtitle1" fontWeight={isMaster ? 600 : 400}>
                        {row.label}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {row.description}
                      </Typography>
                      {row.disabledByMaster && (
                        <Typography variant="body2" color="warning.main" sx={{ mt: 1 }}>
                          Off while AI master switch is disabled (stored setting:{' '}
                          {row.storedEnabled ? 'on' : 'off'}).
                        </Typography>
                      )}
                      {row.aiFeature && masterOff && !row.disabledByMaster && row.storedEnabled && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          Stored as on; will apply when the master switch is turned on.
                        </Typography>
                      )}
                      <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 1 }}>
                        {row.key}
                      </Typography>
                    </Box>
                  </Paper>
                );
              })}
            </Stack>
            <Divider sx={{ my: 3 }} />
            <Typography variant="h6" sx={{ mb: 1 }}>Limits</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              System-wide limit defaults. These apply to every user without a tier, and they are the value a tier falls
              back to for any limit it does not override. Changing a default takes effect immediately for users on the
              global baseline.
            </Typography>
            <Stack spacing={1.5}>
              {limitDefaultRows.map((row) => {
                const draft = limitDefaultDrafts[row.key] ?? String(row.value);
                const parsed = Number(draft);
                const invalid = draft === '' || Number.isNaN(parsed) || parsed < 0;
                const unchanged = String(row.value) === draft.trim();
                return (
                  <Paper key={row.key} variant="outlined" sx={{ p: 1.5 }}>
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      spacing={1.5}
                      alignItems={{ sm: 'center' }}
                      justifyContent="space-between"
                    >
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="subtitle2">{row.label}</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                          {row.description}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.disabled"
                          sx={{ display: 'block', mt: 0.5 }}
                        >
                          {row.key}
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <TextField
                          size="small"
                          type="number"
                          value={draft}
                          error={invalid}
                          onChange={(e) =>
                            setLimitDefaultDrafts((prev) => ({ ...prev, [row.key]: e.target.value }))
                          }
                          inputProps={{ min: 0, 'aria-label': `${row.label} value` }}
                          sx={{ width: 140 }}
                        />
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => void handleLimitDefaultSave(row.key)}
                          disabled={limitDefaultSavingKey !== null || invalid || unchanged}
                        >
                          {limitDefaultSavingKey === row.key ? <CircularProgress size={18} /> : 'Save'}
                        </Button>
                      </Stack>
                    </Stack>
                  </Paper>
                );
              })}
              {limitDefaultRows.length === 0 && (
                <Typography variant="body2" color="text.secondary">No limits defined.</Typography>
              )}
            </Stack>
            </>
          )}
          {adminTab === 3 && (
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', flexShrink: 0 }}>
              <TextField
                variant="outlined"
                placeholder="Search by username, email, or user ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                size="small"
                sx={{ width: { xs: '100%', sm: 320 } }}
              />
              {usersSubTab !== 0 && (
                <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: { xs: '100%', sm: 280 } }}>
                  <Typography
                    component="label"
                    htmlFor="admin-user-columns-select"
                    variant="body2"
                    color="text.secondary"
                    sx={{ whiteSpace: 'nowrap', lineHeight: 1.2 }}
                  >
                    Extra columns
                  </Typography>
                  <Select<OptionalUserColumn[]>
                    id="admin-user-columns-select"
                    size="small"
                    variant="outlined"
                    multiple
                    value={optionalColumnsSelectValue}
                    onChange={handleOptionalColumnsChange}
                    renderValue={renderOptionalColumnsValue}
                    sx={{ flex: 1, minWidth: 160, height: 40 }}
                  >
                    {OPTIONAL_USER_COLUMNS.map((col) => (
                      <MenuItem key={col.key} value={col.key}>
                        <Checkbox size="small" checked={visibleOptionalColumns.has(col.key)} sx={{ py: 0 }} />
                        <ListItemText primary={col.label} />
                      </MenuItem>
                    ))}
                  </Select>
                </Stack>
              )}
            </Box>

            <Tabs
              value={usersSubTab}
              onChange={(_, v) => setUsersSubTab(v as number)}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              aria-label="User groups"
              sx={{ borderBottom: 1, borderColor: 'divider', flexShrink: 0, mb: 1 }}
            >
              <Tab label={`Online (${onlineUsers.length})`} />
              <Tab label={`Users (${regularUsersList.length})`} />
              <Tab label={`Administrators (${adminUsersList.length})`} />
            </Tabs>

            <TableContainer
              sx={{
                flex: 1,
                minHeight: 280,
                overflow: 'auto',
                border: (theme) => `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
              }}
            >
              {usersSubTab === 0 ? (
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>User ID</TableCell>
                      <TableCell>Username</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Provider</TableCell>
                      <TableCell>Login Time</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredOnlineUsers.map((user) => (
                      <TableRow key={user.id}>
                        <TableCell sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>{user.id}</TableCell>
                        <TableCell>{user.username}</TableCell>
                        <TableCell
                          sx={{ maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis' }}
                          title={user.email}
                        >
                          {user.email || '—'}
                        </TableCell>
                        <TableCell>{user.provider}</TableCell>
                        <TableCell>{formatServerLocal(user.loginTime, 'YYYY-MM-DD HH:mm:ss')}</TableCell>
                      </TableRow>
                    ))}
                    {filteredOnlineUsers.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          {onlineUsers.length === 0
                            ? 'No users currently online'
                            : 'No users match your search'}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              ) : (
                (() => {
                  const groupUsers = usersSubTab === 2 ? adminUsersList : regularUsersList;
                  const visibleRows = getSortedUsers(filterUsers(groupUsers, searchTerm));
                  const accountColSpan = 8 + visibleOptionalColumns.size;
                  return (
                    <Table stickyHeader size="small">
                      <TableHead>{renderAccountUserTableHead()}</TableHead>
                      <TableBody>
                        {visibleRows.map((user) => (
                          <TableRow key={user.id}>
                            <TableCell sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>{user.id}</TableCell>
                            <TableCell>{user.username}</TableCell>
                            {renderOptionalUserCells(user)}
                            <TableCell>{user.provider}</TableCell>
                            <TableCell>{formatServerLocal(user.creationDate, 'YYYY-MM-DD HH:mm')}</TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                icon={getExpirationStatus(user.expirationDate, user.accountNeverExpires).icon}
                                label={getExpirationStatus(user.expirationDate, user.accountNeverExpires).text}
                                color={
                                  getExpirationStatus(user.expirationDate, user.accountNeverExpires).color as
                                    | 'success'
                                    | 'warning'
                                    | 'error'
                                }
                                sx={{ '& .MuiChip-icon': { color: 'inherit' } }}
                              />
                            </TableCell>
                            <TableCell>
                              {user.lastLoginAt ? formatServerLocal(user.lastLoginAt, 'YYYY-MM-DD HH:mm') : 'Never'}
                            </TableCell>
                            <TableCell>
                              <Box
                                component="span"
                                sx={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 1,
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                <Box
                                  sx={{
                                    width: 10,
                                    height: 10,
                                    borderRadius: '50%',
                                    flexShrink: 0,
                                    backgroundColor: isUserOnline(user.username) ? 'success.main' : 'grey.500',
                                  }}
                                />
                                {isUserOnline(user.username) ? 'Online' : 'Offline'}
                              </Box>
                            </TableCell>
                            <TableCell align="right">
                              <IconButton
                                onClick={(e) => handleMenuOpen(e, user)}
                                aria-label={`Actions for ${user.username}`}
                              >
                                <MoreVertIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                        {visibleRows.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={accountColSpan} align="center">
                              {groupUsers.length === 0
                                ? 'No users in this group'
                                : 'No users match your search'}
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  );
                })()
              )}
            </TableContainer>
          </Box>
          )}
          {adminTab === 4 && (
          <>
            <Box sx={{ mb: 2 }}>
              <TextField
                variant="outlined"
                placeholder="Search by document name, user, email, or id…"
                value={openDocumentsSearch}
                onChange={e => setOpenDocumentsSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                size="small"
                sx={{ width: { xs: '100%', sm: 400 } }}
              />
            </Box>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{renderOpenDocumentSortLabel('name', 'Document Name')}</TableCell>
                    <TableCell>{renderOpenDocumentSortLabel('user', 'User')}</TableCell>
                    <TableCell>{renderOpenDocumentSortLabel('creationDate', 'Created')}</TableCell>
                    <TableCell>{renderOpenDocumentSortLabel('updatedAt', 'Last Updated')}</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredOpenDocuments.map((doc) => (
                    <TableRow key={doc.id}>
                      <TableCell>{doc.name}</TableCell>
                      <TableCell>{doc.user.username}</TableCell>
                      <TableCell>
                        {doc.creationDate ? 
                          formatServerLocal(doc.creationDate, 'MMM D, YYYY HH:mm:ss') : 
                          'N/A'
                        }
                      </TableCell>
                      <TableCell>
                        {doc.updatedAt ? 
                          formatServerLocal(doc.updatedAt, 'MMM D, YYYY HH:mm:ss') : 
                          'N/A'
                        }
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap" useFlexGap>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<VisibilityIcon />}
                            onClick={() => handleViewDocumentReadOnly(doc.user.id, doc.name)}
                            disabled={viewingDocumentKey === `${doc.user.id}:${doc.name}`}
                          >
                            {viewingDocumentKey === `${doc.user.id}:${doc.name}` ? 'Opening…' : 'View (read-only)'}
                          </Button>
                          <Button
                            variant="outlined"
                            color="success"
                            size="small"
                            onClick={() => setSaveToDbDoc(doc)}
                          >
                            Save to DB
                          </Button>
                          <Button
                            variant="outlined"
                            color="primary"
                            size="small"
                            onClick={() => setReloadCacheDoc(doc)}
                          >
                            Reload cache
                          </Button>
                          <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            onClick={() => handleCloseDocument(String(doc.id))}
                          >
                            Close
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                  {openDocuments.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        No open documents
                      </TableCell>
                    </TableRow>
                  )}
                  {openDocuments.length > 0 && filteredOpenDocuments.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        No documents match your search
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </>
          )}
          {adminTab === ADMIN_TAB_DOCUMENT_ACTIVITY && (
          <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
            <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, flexShrink: 0 }}>
              <Autocomplete
                size="small"
                sx={{ minWidth: 280 }}
                options={activityUserOptions}
                value={activityUserSelection}
                onChange={(_, option) => setActivityUserSelection(option)}
                getOptionLabel={(option) =>
                  option.scope === 'all' ? option.label : option.user.username}
                isOptionEqualToValue={(a, b) => {
                  if (a.scope === 'all' && b.scope === 'all') {
                    return true;
                  }
                  if (a.scope === 'user' && b.scope === 'user') {
                    return a.user.id === b.user.id;
                  }
                  return false;
                }}
                renderInput={(params) => (
                  <TextField {...params} label="User" placeholder="Select a user or All…" required />
                )}
              />
              <Typography variant="body2" color="text.secondary">
                {activityUserSelection?.scope === 'all'
                  ? 'Recent document edits across all users (kept 7 days). Refreshes every 30 seconds while this tab is open.'
                  : 'Recent document edits for the selected user (kept 7 days). Refreshes every 30 seconds while this tab is open.'}
              </Typography>
            </Box>
            {activityUserSelection ? (
              <DocumentActivityTable
                userId={
                  activityUserSelection.scope === 'user'
                    ? activityUserSelection.user.id
                    : undefined
                }
                autoRefresh
              />
            ) : (
              <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="text.secondary">
                  Select a user (or All users) above to view document activity.
                </Typography>
              </Paper>
            )}
          </Box>
          )}
          {adminTab === 6 && (
            <AuditLogsTable />
          )}
          {adminTab === 7 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
            <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, flexShrink: 0 }}>
              <Button
                variant="contained"
                startIcon={<CampaignIcon />}
                onClick={() => {
                  resetAnnouncementForm();
                  setCreateAnnouncementOpen(true);
                }}
              >
                New announcement
              </Button>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Switch
                  checked={showAnnouncementHistory}
                  onChange={(_, checked) => setShowAnnouncementHistory(checked)}
                  inputProps={{ 'aria-label': 'Show announcement history' }}
                />
                <Typography variant="body2">Show history</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ flex: 1, minWidth: 240 }}>
                Logged-in users see active announcements within about 30 seconds. New sessions see them immediately.
              </Typography>
            </Box>
            {announcementsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer component={Paper} sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Status</TableCell>
                      <TableCell>Severity</TableCell>
                      <TableCell>Audience</TableCell>
                      <TableCell>Message</TableCell>
                      <TableCell>Window</TableCell>
                      <TableCell>Created</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {visibleAnnouncements.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          {showAnnouncementHistory
                            ? 'No announcements yet.'
                            : 'No live or scheduled announcements. Turn on Show history to see inactive and expired items.'}
                        </TableCell>
                      </TableRow>
                    )}
                    {visibleAnnouncements.map((announcement) => {
                      const { expired, statusLabel, statusColor } = getAnnouncementStatus(announcement);
                      const canDeactivate = announcement.active && !expired;
                      const canReactivate = !announcement.active && !expired;
                      return (
                        <TableRow key={announcement.id}>
                          <TableCell>
                            <Chip size="small" label={statusLabel} color={statusColor} />
                          </TableCell>
                          <TableCell>{announcement.severity}</TableCell>
                          <TableCell>
                            {announcement.audience === 'SPECIFIC'
                              ? `Specific (${(announcement.targetUsernames ?? []).join(', ') || '—'})`
                              : 'All users'}
                          </TableCell>
                          <TableCell sx={{ maxWidth: 360 }}>
                            {announcement.title ? (
                              <>
                                <strong>{announcement.title}</strong>
                                {': '}
                              </>
                            ) : null}
                            {announcement.message}
                          </TableCell>
                          <TableCell>
                            {announcement.startsAt
                              ? formatServerLocal(announcement.startsAt, 'MMM D, YYYY HH:mm')
                              : 'Now'}
                            {' → '}
                            {announcement.endsAt
                              ? formatServerLocal(announcement.endsAt, 'MMM D, YYYY HH:mm')
                              : 'No end'}
                          </TableCell>
                          <TableCell>
                            {announcement.createdAt
                              ? formatServerLocal(announcement.createdAt, 'MMM D, YYYY HH:mm')
                              : '—'}
                            {announcement.createdByUsername ? (
                              <Typography variant="caption" display="block" color="text.secondary">
                                by {announcement.createdByUsername}
                              </Typography>
                            ) : null}
                          </TableCell>
                          <TableCell align="right">
                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                              {canDeactivate && (
                                <Button
                                  size="small"
                                  color="warning"
                                  onClick={() => void handleDeactivateAnnouncement(announcement.id)}
                                >
                                  Deactivate
                                </Button>
                              )}
                              {canReactivate && (
                                <Button
                                  size="small"
                                  color="primary"
                                  onClick={() => void handleReactivateAnnouncement(announcement.id)}
                                >
                                  Reactivate
                                </Button>
                              )}
                              <Button
                                size="small"
                                color="error"
                                onClick={() => setDeleteAnnouncement(announcement)}
                              >
                                Delete
                              </Button>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
          )}
          {adminTab === 8 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
            <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', gap: 1, flexShrink: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Button variant="contained" onClick={runSanityChecks} disabled={loadingSanity || repairingAll}>
                {loadingSanity && !repairingAll ? <CircularProgress size={20} /> : 'Run Sanity Checks'}
              </Button>
              <Button
                variant="outlined"
                color="error"
                onClick={handleRepairAll}
                disabled={repairingAll || loadingSanity || !sanityReport?.results?.some((r: any) => r.repairable)}
                startIcon={repairingAll ? <CircularProgress size={16} /> : null}
              >
                {repairingAll
                  ? repairAllProgress?.phase === 'refreshing'
                    ? 'Refreshing…'
                    : `Repairing ${repairAllProgress?.completed ?? 0} / ${repairAllProgress?.total ?? 0}…`
                  : 'Repair All'}
              </Button>
              {sanityReport?.runAt && !repairingAll && (
                <Typography variant="body2" color="text.secondary">
                  Last Run: {formatAdminLocalDateTime(sanityReport.runAt)}
                </Typography>
              )}
              </Box>
              {repairingAll && repairAllProgress && (
                <Box sx={{ width: '100%', maxWidth: 480 }}>
                  <LinearProgress
                    variant={
                      repairAllProgress.phase === 'refreshing' ? 'indeterminate' : 'determinate'
                    }
                    value={
                      repairAllProgress.total > 0
                        ? (repairAllProgress.completed / repairAllProgress.total) * 100
                        : 0
                    }
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    {repairAllProgress.phase === 'refreshing'
                      ? 'Re-running sanity checks…'
                      : repairAllProgress.currentCheck
                        ? `Repairing: ${repairAllProgress.currentCheck} (${repairAllProgress.completed} of ${repairAllProgress.total})`
                        : `Repairing ${repairAllProgress.completed} of ${repairAllProgress.total}…`}
                  </Typography>
                </Box>
              )}
            </Box>
            <TableContainer component={Paper} sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Check</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Details</TableCell>
                    <TableCell>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {!loadingSanity && !sanityReport && (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        No report loaded. Click Run Sanity Checks to scan open documents in Redis.
                      </TableCell>
                    </TableRow>
                  )}
                  {sanityReport?.results?.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        No issues found in open documents.
                      </TableCell>
                    </TableRow>
                  )}
                  {sanityReport?.results?.map((result: any, idx: number) => (
                    <TableRow key={idx}>
                      <TableCell>{result.checkName}</TableCell>
                      <TableCell>
                        {result.status === 'OK' && <CheckCircleIcon sx={{ color: 'green', verticalAlign: 'middle', mr: 1 }} />}
                        {result.status === 'WARNING' && <WarningIcon sx={{ color: 'orange', verticalAlign: 'middle', mr: 1 }} />}
                        {result.status === 'ERROR' && <ErrorIcon sx={{ color: 'red', verticalAlign: 'middle', mr: 1 }} />}
                        {result.status}
                      </TableCell>
                      <TableCell>{result.details}</TableCell>
                      <TableCell>
                        {result.repairable && (
                          <Button
                            size="small"
                            color="error"
                            onClick={() => handleRepair(result.checkName, result.repairId)}
                            disabled={repairingAll || repairingId === result.repairId}
                            startIcon={repairingId === result.repairId ? <CircularProgress size={16} /> : null}
                          >
                            Repair
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
          )}
          {adminTab === ADMIN_TAB_TIERS && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Typography variant="body2" color="text.secondary">
              Tiers control both numeric limits and feature flags per user. A new tier starts by inheriting every global
              default; you only override what should differ. A flag left as “Inherit” uses the global Feature flags
              value; set it to On/Off to override it for everyone in this tier. To give a lower tier no AI features,
              override “AI features (master)” to Off for that tier. Users with no tier use the global defaults.
            </Typography>

            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 1.5 }}>Create tier</Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
                <TextField
                  label="Tier name"
                  size="small"
                  value={newTierName}
                  onChange={(e) => setNewTierName(e.target.value)}
                  sx={{ minWidth: 200 }}
                />
                <TextField
                  label="Description (optional)"
                  size="small"
                  value={newTierDescription}
                  onChange={(e) => setNewTierDescription(e.target.value)}
                  sx={{ flex: 1, minWidth: 240 }}
                />
                <Button
                  variant="contained"
                  onClick={() => void handleCreateTier()}
                  disabled={creatingTier || !newTierName.trim()}
                >
                  {creatingTier ? <CircularProgress size={20} /> : 'Create'}
                </Button>
              </Stack>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 1.5 }}>Assign user to tier</Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
                <Select
                  size="small"
                  displayEmpty
                  value={assignUserId === '' ? '' : String(assignUserId)}
                  onChange={(e) => setAssignUserId(e.target.value === '' ? '' : Number(e.target.value))}
                  sx={{ minWidth: 220 }}
                >
                  <MenuItem value=""><em>Select user…</em></MenuItem>
                  {users.map((u) => (
                    <MenuItem key={u.id} value={String(u.id)}>{u.username}</MenuItem>
                  ))}
                </Select>
                <Select
                  size="small"
                  displayEmpty
                  value={assignTierId === '' ? '' : String(assignTierId)}
                  onChange={(e) => setAssignTierId(e.target.value === '' ? '' : Number(e.target.value))}
                  sx={{ minWidth: 220 }}
                >
                  <MenuItem value=""><em>No tier (global defaults)</em></MenuItem>
                  {tiers.map((t) => (
                    <MenuItem key={t.id} value={String(t.id)}>{t.name}</MenuItem>
                  ))}
                </Select>
                <Button
                  variant="contained"
                  onClick={() => void handleAssignUserToTier()}
                  disabled={assigningTier || assignUserId === ''}
                >
                  {assigningTier ? <CircularProgress size={20} /> : 'Assign'}
                </Button>
              </Stack>
            </Paper>

            <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' }, alignItems: 'stretch' }}>
              <Paper variant="outlined" sx={{ p: 2, minWidth: { md: 260 } }}>
                <Typography variant="subtitle1" sx={{ mb: 1.5 }}>Tiers</Typography>
                <Stack spacing={1}>
                  {tiers.map((t) => (
                    <Paper
                      key={t.id}
                      variant="outlined"
                      onClick={() => setSelectedTierId(t.id)}
                      sx={{
                        p: 1.5,
                        cursor: 'pointer',
                        ...(selectedTierId === t.id
                          ? { borderColor: 'primary.main', borderWidth: 2 }
                          : {}),
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                        <Typography variant="subtitle2">{t.name}</Typography>
                        <IconButton
                          size="small"
                          aria-label={`Delete tier ${t.name}`}
                          disabled={t.userCount > 0}
                          title={
                            t.userCount > 0
                              ? 'Remove all users from this tier before deleting'
                              : 'Delete tier'
                          }
                          onClick={(e) => {
                            e.stopPropagation();
                            void handleDeleteTier(t);
                          }}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                      {t.description && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          {t.description}
                        </Typography>
                      )}
                      <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
                        {t.userCount} user{t.userCount === 1 ? '' : 's'}
                      </Typography>
                    </Paper>
                  ))}
                  {tiers.length === 0 && (
                    <Typography variant="body2" color="text.secondary">No tiers yet.</Typography>
                  )}
                </Stack>
              </Paper>

              <Paper variant="outlined" sx={{ p: 2, flex: 1, minWidth: 0 }}>
                {selectedTierId == null ? (
                  <Typography variant="body2" color="text.secondary">
                    Select a tier to edit its feature-flag overrides.
                  </Typography>
                ) : (
                  <>
                    {tierUsers.length > 0 && (
                      <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                        <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
                          Assigned:
                        </Typography>
                        {tierUsers.map((u) => (
                          <Chip key={u} size="small" label={u} />
                        ))}
                      </Box>
                    )}
                    <Typography variant="subtitle1" sx={{ mb: 1 }}>
                      Limits for{' '}
                      {tiers.find((t) => t.id === selectedTierId)?.name ?? `tier ${selectedTierId}`}
                    </Typography>
                    <Stack spacing={1.5} sx={{ mb: 3 }}>
                      {tierLimits.map((row) => {
                        const draft = tierLimitDrafts[row.key] ?? String(row.value);
                        const parsed = Number(draft);
                        const invalid = draft === '' || Number.isNaN(parsed) || parsed < 0;
                        const unchanged = String(row.value) === draft.trim();
                        return (
                          <Paper key={row.key} variant="outlined" sx={{ p: 1.5 }}>
                            <Stack
                              direction={{ xs: 'column', sm: 'row' }}
                              spacing={1.5}
                              alignItems={{ sm: 'center' }}
                              justifyContent="space-between"
                            >
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="subtitle2">{row.label}</Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                                  {row.description}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.disabled"
                                  sx={{ display: 'block', mt: 0.5 }}
                                >
                                  {row.key} · system default: {row.systemDefault.toLocaleString()}
                                </Typography>
                              </Box>
                              <Stack direction="row" spacing={1} alignItems="center">
                                <TextField
                                  size="small"
                                  type="number"
                                  value={draft}
                                  error={invalid}
                                  onChange={(e) =>
                                    setTierLimitDrafts((prev) => ({ ...prev, [row.key]: e.target.value }))
                                  }
                                  inputProps={{ min: 0, 'aria-label': `${row.label} value` }}
                                  sx={{ width: 140 }}
                                />
                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={() => void handleTierLimitSave(selectedTierId, row.key)}
                                  disabled={tierLimitSavingKey !== null || invalid || unchanged}
                                >
                                  {tierLimitSavingKey === row.key ? <CircularProgress size={18} /> : 'Save'}
                                </Button>
                              </Stack>
                            </Stack>
                          </Paper>
                        );
                      })}
                    </Stack>
                    <Typography variant="subtitle1" sx={{ mb: 1 }}>
                      Feature flags for{' '}
                      {tiers.find((t) => t.id === selectedTierId)?.name ?? `tier ${selectedTierId}`}
                    </Typography>
                    <Stack spacing={1.5}>
                      {tierFlags.map((row) => {
                        const mode: 'inherit' | 'on' | 'off' =
                          row.override === null ? 'inherit' : row.override ? 'on' : 'off';
                        return (
                          <Paper key={row.key} variant="outlined" sx={{ p: 1.5 }}>
                            <Stack
                              direction={{ xs: 'column', sm: 'row' }}
                              spacing={1.5}
                              alignItems={{ sm: 'center' }}
                              justifyContent="space-between"
                            >
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="subtitle2">
                                  {row.label}
                                  {row.key === 'AI_FEATURES_MASTER' && (
                                    <Chip size="small" label="AI master" sx={{ ml: 1 }} />
                                  )}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                                  {row.description}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.disabled"
                                  sx={{ display: 'block', mt: 0.5 }}
                                >
                                  {row.key} · effective for this tier: {row.effective ? 'On' : 'Off'}
                                </Typography>
                              </Box>
                              <Select
                                size="small"
                                value={mode}
                                onChange={(e) =>
                                  void handleTierFlagChange(
                                    selectedTierId,
                                    row.key,
                                    e.target.value as 'inherit' | 'on' | 'off',
                                  )
                                }
                                disabled={tierFlagSavingKey !== null}
                                sx={{ minWidth: 190 }}
                              >
                                <MenuItem value="inherit">
                                  Inherit (global: {row.globalEnabled ? 'On' : 'Off'})
                                </MenuItem>
                                <MenuItem value="on">On</MenuItem>
                                <MenuItem value="off">Off</MenuItem>
                              </Select>
                            </Stack>
                          </Paper>
                        );
                      })}
                    </Stack>
                  </>
                )}
              </Paper>
            </Box>
          </Box>
          )}
        </Box>

        {/* Actions Menu */}
        <Menu
          anchorEl={menuAnchorEl}
          open={Boolean(menuAnchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={() => handleAction('view-details')}>
            View Details
          </MenuItem>
          <MenuItem onClick={() => handleAction('view-documents')}>
            View Documents
          </MenuItem>
          <MenuItem onClick={() => handleAction('view-activity')}>
            View document activity
          </MenuItem>
          {selectedUser && !selectedUser.admin && !isCurrentUser(selectedUser) && (
            <MenuItem
              onClick={() => handleAction('impersonate')}
              disabled={impersonatingUserId === selectedUser.id}
            >
              <VisibilityIcon fontSize="small" sx={{ mr: 1 }} />
              {impersonatingUserId === selectedUser.id ? 'Opening…' : 'View as user (read-only)'}
            </MenuItem>
          )}
          {!selectedUser?.admin && (
            <MenuItem onClick={() => handleAction('make-admin')}>
              Make Admin
            </MenuItem>
          )}
          {selectedUser?.admin && !isCurrentUser(selectedUser) && (
            <MenuItem onClick={() => handleAction('remove-admin')}>
              Remove Admin
            </MenuItem>
          )}
          <MenuItem onClick={() => handleAction('set-tier')}>
            Set tier…
          </MenuItem>
          {selectedUser?.tierId != null && (
            <MenuItem onClick={() => handleAction('remove-tier')}>
              Remove from tier
            </MenuItem>
          )}
          <MenuItem 
            onClick={() => handleAction('expiration')}
            disabled={Boolean(selectedUser && isCurrentUser(selectedUser))}
          >
            Set Expiration
          </MenuItem>
          {!selectedUser?.accountNeverExpires && (
            <MenuItem 
              onClick={() => handleAction('set-never-expire')}
              disabled={Boolean(selectedUser && isCurrentUser(selectedUser))}
            >
              Set Never Expire
            </MenuItem>
          )}
          {selectedUser?.provider === 'LOCAL' && (
            <MenuItem 
              onClick={() => handleAction('reset')}
              disabled={Boolean(selectedUser && isCurrentUser(selectedUser))}
            >
              Reset Password
            </MenuItem>
          )}
          <MenuItem 
            onClick={() => handleAction('delete')} 
            disabled={Boolean(selectedUser && isCurrentUser(selectedUser))}
            sx={{ color: 'error.main' }}
          >
            Delete User
          </MenuItem>
        </Menu>

        {/* Set Tier Dialog */}
        <Dialog open={!!setTierUser} onClose={() => setSetTierUser(null)} fullWidth maxWidth="xs">
          <DialogTitle>Set tier for {setTierUser?.username}</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              Choose a tier for this user. “Global defaults (no tier)” removes any tier so the user falls back to the
              system-wide limits and feature flags.
            </DialogContentText>
            <Select
              fullWidth
              size="small"
              displayEmpty
              value={setTierSelection === '' ? '' : String(setTierSelection)}
              onChange={(e) =>
                setSetTierSelection(e.target.value === '' ? '' : Number(e.target.value))
              }
            >
              <MenuItem value="">Global defaults (no tier)</MenuItem>
              {tiers.map((t) => (
                <MenuItem key={t.id} value={String(t.id)}>
                  {t.name}
                </MenuItem>
              ))}
            </Select>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSetTierUser(null)} disabled={savingUserTier}>
              Cancel
            </Button>
            <Button variant="contained" onClick={() => void handleSaveUserTier()} disabled={savingUserTier}>
              {savingUserTier ? <CircularProgress size={20} /> : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Set Expiration Dialog */}
        <Dialog open={!!editUser} onClose={() => setEditUser(null)}>
          <DialogTitle>Set Expiration for {editUser?.username}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Enter the new expiration date and time (leave empty for no expiration):
            </DialogContentText>
            <TextField
              type="datetime-local"
              fullWidth
              value={newExpiration}
              onChange={e => setNewExpiration(e.target.value)}
              sx={{ mt: 2 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditUser(null)}>Cancel</Button>
            <Button onClick={handleSetExpiration} variant="contained">Save</Button>
          </DialogActions>
        </Dialog>

        <Dialog open={!!saveToDbDoc} onClose={() => !saveToDbLoading && setSaveToDbDoc(null)}>
          <DialogTitle>Save document to database?</DialogTitle>
          <DialogContent>
            <Alert severity="info" sx={{ mb: 2 }}>
              Writes the current server cache (Redis) into the document blob in the database. If there are unpersisted
              edits, the previous blob is stored as a revision checkpoint first—the same behavior as the periodic save
              task.
            </Alert>
            <DialogContentText>
              Document: <strong>{saveToDbDoc?.name}</strong> (user {saveToDbDoc?.user.username})
            </DialogContentText>
            <DialogContentText sx={{ mt: 2, color: 'text.secondary' }}>
              If nothing changed since the last save, no write occurs and you will see an “already matches” message.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSaveToDbDoc(null)} disabled={saveToDbLoading}>
              Cancel
            </Button>
            <Button
              onClick={confirmPersistOpenDocumentToDatabase}
              color="success"
              variant="contained"
              disabled={saveToDbLoading}
            >
              {saveToDbLoading ? 'Saving…' : 'Save to database'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={!!reloadCacheDoc} onClose={() => !reloadCacheLoading && setReloadCacheDoc(null)}>
          <DialogTitle>Reload document cache from database?</DialogTitle>
          <DialogContent>
            <Alert severity="warning" sx={{ mb: 2 }}>
              This replaces the server Redis cache for this document with the last data saved in the database.
              Any changes present only in the cache (not yet persisted) will be lost.
            </Alert>
            <DialogContentText>
              Document: <strong>{reloadCacheDoc?.name}</strong> (user {reloadCacheDoc?.user.username})
            </DialogContentText>
            <DialogContentText sx={{ mt: 2, color: 'text.secondary' }}>
              Users who have this document open may need to refresh or reopen it to see the updated tree.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setReloadCacheDoc(null)} disabled={reloadCacheLoading}>
              Cancel
            </Button>
            <Button
              onClick={confirmReloadOpenDocumentCache}
              color="primary"
              variant="contained"
              disabled={reloadCacheLoading}
            >
              {reloadCacheLoading ? 'Reloading…' : 'Reload cache'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete User Dialog */}
        <Dialog open={!!deleteUser} onClose={() => setDeleteUser(null)}>
          <DialogTitle sx={{ color: 'error.main' }}>Delete User</DialogTitle>
          <DialogContent>
            <Alert severity="error" sx={{ mb: 2 }}>
              Warning: This action cannot be undone.
            </Alert>
            <DialogContentText>
              Are you sure you want to delete user <strong>{deleteUser?.username}</strong>?
            </DialogContentText>
            <DialogContentText sx={{ mt: 2, color: 'text.secondary' }}>
              This will permanently remove the user's account and all associated data from the system.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteUser(null)}>Cancel</Button>
            <Button 
              onClick={handleDeleteUser} 
              color="error" 
              variant="contained"
              sx={{ 
                '&:hover': {
                  backgroundColor: 'error.dark'
                }
              }}
            >
              Delete User
            </Button>
          </DialogActions>
        </Dialog>

        {/* Reset Password Dialog */}
        <Dialog open={!!resetPasswordUser} onClose={() => setResetPasswordUser(null)}>
          <DialogTitle>Reset Password</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to send a password reset email to {resetPasswordUser?.username}?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setResetPasswordUser(null)}>Cancel</Button>
            <Button onClick={handleResetPassword} color="primary" variant="contained">Send Reset Email</Button>
          </DialogActions>
        </Dialog>

        {/* Purge content media staging */}
        <Dialog
          open={stagingPurgeDialogOpen}
          onClose={() => !stagingPurgeLoading && setStagingPurgeDialogOpen(false)}
        >
          <DialogTitle>Purge stale staging uploads?</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              Remove unfinished uploads under the staging folder when both the data file and sidecar (if any) are older
              than the age below. Saved attachments under stored content paths are not removed.
              {stats?.mediaStagingPurgeEnabled === false && (
                <span> The daily scheduled purge is disabled in configuration; this still runs on demand.</span>
              )}
            </DialogContentText>
            <TextField
              label="Minimum age (days)"
              type="number"
              fullWidth
              size="small"
              inputProps={{ min: 1, max: 3650, step: 1 }}
              value={stagingPurgeRetentionDaysInput}
              onChange={(e) => setStagingPurgeRetentionDaysInput(e.target.value)}
              disabled={stagingPurgeLoading}
              helperText="Files with last-modified newer than this many days are kept. Defaults from server config when you open this dialog; change it to purge more aggressively (e.g. 1)."
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setStagingPurgeDialogOpen(false)} disabled={stagingPurgeLoading}>
              Cancel
            </Button>
            <Button onClick={confirmStagingPurge} variant="contained" disabled={stagingPurgeLoading}>
              {stagingPurgeLoading ? 'Purging…' : 'Purge now'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={changePasswordOpen} onClose={() => setChangePasswordOpen(false)}>
          <DialogTitle>Change Password</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              Please enter your current password and choose a new password.
            </DialogContentText>
            <TextField
              autoFocus
              margin="dense"
              label="Current Password"
              type="password"
              fullWidth
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              sx={{ mb: 2 }}
            />
            <TextField
              margin="dense"
              label="New Password"
              type="password"
              fullWidth
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setChangePasswordOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleChangePassword} 
              variant="contained"
              disabled={!currentPassword || !newPassword}
            >
              Change Password
            </Button>
          </DialogActions>
        </Dialog>

        {/* View Details Dialog */}
        <Dialog 
          open={!!viewDetailsUser || loadingDetails || !!detailsError} 
          onClose={() => {
            setViewDetailsUser(null);
            setDetailsError(null);
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>User Details</DialogTitle>
          <DialogContent>
            {loadingDetails ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : detailsError ? (
              <Alert severity="error" sx={{ mt: 2 }}>
                {detailsError}
              </Alert>
            ) : viewDetailsUser && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>User ID</Typography>
                <Typography variant="body1" sx={{ mb: 2, fontFamily: 'monospace' }}>{viewDetailsUser.id}</Typography>

                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>Username</Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>{viewDetailsUser.username}</Typography>

                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>Email</Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>{viewDetailsUser.email}</Typography>

                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>Name</Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {viewDetailsUser.firstName} {viewDetailsUser.lastName}
                </Typography>

                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>Account Status</Typography>
                <Typography variant="body1" color={viewDetailsUser.accountNeverExpires || (viewDetailsUser.expirationDate && serverDateTimeDayjs(viewDetailsUser.expirationDate).isAfter(dayjs())) ? 'success.main' : 'error.main'} sx={{ mb: 2 }}>
                  {viewDetailsUser.accountNeverExpires || (viewDetailsUser.expirationDate && serverDateTimeDayjs(viewDetailsUser.expirationDate).isAfter(dayjs())) ? 'Active' : 'Expired'}
                </Typography>

                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>Expiration</Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {viewDetailsUser.accountNeverExpires ? 'Never' : (
                    viewDetailsUser.expirationDate ?
                      formatServerLocal(viewDetailsUser.expirationDate, 'YYYY-MM-DD HH:mm') :
                      'Never'
                  )}
                </Typography>

                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>Created</Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {viewDetailsUser.creationDate ?
                    formatServerLocal(viewDetailsUser.creationDate, 'YYYY-MM-DD HH:mm:ss') :
                    'N/A'}
                </Typography>

                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>Provider</Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {viewDetailsUser.provider || 'N/A'}
                </Typography>

                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>Last Login</Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {viewDetailsUser.lastLoginAt ?
                    formatServerLocal(viewDetailsUser.lastLoginAt, 'YYYY-MM-DD HH:mm:ss') :
                    'Never'}
                </Typography>

                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>Documents</Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {viewDetailsUser.documentCount || 0} documents
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setViewDetailsUser(null);
              setDetailsError(null);
            }}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* View Documents Dialog */}
        <Dialog 
          open={!!viewDocumentsUser} 
          onClose={() => {
            setViewDocumentsUser(null);
            setUserDocuments([]);
            setViewRevisionsDocument(null);
            setDocumentRevisions([]);
          }}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            Documents for {viewDocumentsUser?.username}
          </DialogTitle>
          <DialogContent>
            {loadingDocuments ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>{renderUserDocumentSortLabel('name', 'Document Name')}</TableCell>
                      <TableCell>{renderUserDocumentSortLabel('revisionCount', 'Revisions')}</TableCell>
                      <TableCell>{renderUserDocumentSortLabel('latestRevisionAt', 'Latest Revision')}</TableCell>
                      <TableCell>{renderUserDocumentSortLabel('creationDate', 'Created')}</TableCell>
                      <TableCell>{renderUserDocumentSortLabel('updatedAt', 'Last Updated')}</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedUserDocuments.map((doc) => (
                      <TableRow key={doc.id}>
                        <TableCell>{doc.name}</TableCell>
                        <TableCell>
                          <Chip
                            label={doc.revisionCount ?? 0}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          {doc.latestRevisionAt ?
                            formatServerLocal(doc.latestRevisionAt, 'MMM D, YYYY HH:mm:ss') :
                            '—'
                          }
                        </TableCell>
                        <TableCell>
                          {doc.creationDate ? 
                            formatServerLocal(doc.creationDate, 'MMM D, YYYY HH:mm:ss') : 
                            'N/A'
                          }
                        </TableCell>
                        <TableCell>
                          {doc.updatedAt ? 
                            formatServerLocal(doc.updatedAt, 'MMM D, YYYY HH:mm:ss') : 
                            'N/A'
                          }
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            onClick={(e) => handleOpenDocumentActions(e, doc)}
                            aria-label="document actions"
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                    {userDocuments.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          No documents found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setViewDocumentsUser(null);
              setUserDocuments([]);
              setViewRevisionsDocument(null);
              setDocumentRevisions([]);
            }}>
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* Document actions menu */}
        <Menu
          anchorEl={documentActionsAnchorEl}
          open={Boolean(documentActionsAnchorEl)}
          onClose={handleCloseDocumentActions}
        >
          <MenuItem
            onClick={() => {
              const doc = documentActionsDoc;
              const owner = viewDocumentsUser;
              handleCloseDocumentActions();
              if (!doc || !owner) return;
              handleViewDocumentReadOnly(owner.id, doc.name);
            }}
            disabled={!documentActionsDoc || !viewDocumentsUser}
          >
            View (read-only)
          </MenuItem>
          <MenuItem
            onClick={() => {
              const doc = documentActionsDoc;
              handleCloseDocumentActions();
              if (!doc) return;
              handleViewRevisions(doc);
            }}
            disabled={!documentActionsDoc || (documentActionsDoc.revisionCount ?? 0) === 0}
          >
            Show revisions
          </MenuItem>
          <MenuItem
            onClick={() => {
              const doc = documentActionsDoc;
              handleCloseDocumentActions();
              if (!doc) return;
              void handleCopyUserDocumentToAdmin(doc);
            }}
            disabled={
              !documentActionsDoc ||
              (documentActionsDoc && copyingDocumentId === String(documentActionsDoc.id))
            }
          >
            {documentActionsDoc && copyingDocumentId === String(documentActionsDoc.id)
              ? 'Copying…'
              : 'Load into my library'}
          </MenuItem>
          <MenuItem
            onClick={() => {
              const doc = documentActionsDoc;
              handleCloseDocumentActions();
              if (!doc) return;
              setDeleteDocument(doc);
            }}
            sx={{ color: 'error.main' }}
          >
            Delete
          </MenuItem>
        </Menu>

        {/* View Revisions Dialog */}
        <Dialog
          open={!!viewRevisionsDocument}
          onClose={() => {
            setViewRevisionsDocument(null);
            setDocumentRevisions([]);
          }}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            Revisions for {viewRevisionsDocument?.name}
          </DialogTitle>
          <DialogContent>
            {loadingRevisions ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <Alert severity="info" sx={{ mb: 2 }}>
                  <strong>Recover for user</strong> adds a new document to {viewDocumentsUser?.username ?? 'this user'}&apos;s
                  library from the revision checkpoint (the original document is unchanged).{' '}
                  <strong>Load into my library</strong> copies the same checkpoint into your admin document list.
                </Alert>
                <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Created</TableCell>
                      <TableCell>Doc Rev</TableCell>
                      <TableCell>Size</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {documentRevisions.map((r) => (
                      <TableRow key={r.id}>
                        <TableCell>
                          {r.createdAt ? formatServerLocal(r.createdAt, 'MMM D, YYYY HH:mm:ss') : 'N/A'}
                        </TableCell>
                        <TableCell>{r.documentRevision}</TableCell>
                        <TableCell>{formatBytes(r.sizeBytes || 0)}</TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap" useFlexGap>
                            <Button
                              variant="contained"
                              size="small"
                              color="primary"
                              onClick={() => setRestoreRevisionConfirm(r)}
                              disabled={
                                copyingRevisionId === r.id ||
                                restoringRevisionId === r.id
                              }
                            >
                              {restoringRevisionId === r.id ? 'Recovering…' : 'Recover for user'}
                            </Button>
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => void handleCopyRevisionToAdmin(r.id)}
                              disabled={
                                copyingRevisionId === r.id ||
                                restoringRevisionId === r.id
                              }
                            >
                              {copyingRevisionId === r.id ? 'Copying…' : 'Load into my library'}
                            </Button>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                    {documentRevisions.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          No revisions found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setViewRevisionsDocument(null);
              setDocumentRevisions([]);
            }}>
              Close
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={!!restoreRevisionConfirm}
          onClose={() => !restoringRevisionId && setRestoreRevisionConfirm(null)}
        >
          <DialogTitle>Recover revision for user?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Create a new document in <strong>{viewDocumentsUser?.username}</strong>&apos;s library from revision{' '}
              <strong>
                {restoreRevisionConfirm?.documentRevision ?? restoreRevisionConfirm?.id}
              </strong>{' '}
              of <strong>{viewRevisionsDocument?.name}</strong>?
            </DialogContentText>
            <DialogContentText sx={{ mt: 2, color: 'text.secondary' }}>
              The original document is not modified. The recovered copy is named with a &quot;(Recovered …)&quot; suffix.
              The user can open it from their document list.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setRestoreRevisionConfirm(null)}
              disabled={!!restoringRevisionId}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              disabled={!restoreRevisionConfirm || !!restoringRevisionId}
              onClick={() => {
                if (!restoreRevisionConfirm) return;
                void handleRestoreRevisionToUser(restoreRevisionConfirm.id);
              }}
            >
              {restoringRevisionId ? 'Recovering…' : 'Recover for user'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Document Confirmation Dialog */}
        <Dialog 
          open={!!deleteDocument} 
          onClose={() => setDeleteDocument(null)}
        >
          <DialogTitle sx={{ color: 'error.main' }}>Delete Document</DialogTitle>
          <DialogContent>
            <Alert severity="error" sx={{ mb: 2 }}>
              Warning: This action cannot be undone.
            </Alert>
            <DialogContentText>
              Are you sure you want to delete document <strong>{deleteDocument?.name}</strong>?
            </DialogContentText>
            <DialogContentText sx={{ mt: 2, color: 'text.secondary' }}>
              This will permanently remove the document and all associated data from the system.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDocument(null)}>Cancel</Button>
            <Button 
              onClick={handleDeleteDocument} 
              color="error" 
              variant="contained"
              sx={{ 
                '&:hover': {
                  backgroundColor: 'error.dark'
                }
              }}
            >
              Delete Document
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={createAnnouncementOpen}
          onClose={() => !announcementSaving && setCreateAnnouncementOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>New announcement</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Template
                </Typography>
                <Select
                  value={announcementTemplateId}
                  onChange={(e: SelectChangeEvent) =>
                    handleAnnouncementTemplateChange(e.target.value as AnnouncementTemplateId | '')
                  }
                  fullWidth
                  displayEmpty
                >
                  <MenuItem value="">Custom announcement</MenuItem>
                  {ANNOUNCEMENT_TEMPLATES.map((template) => (
                    <MenuItem key={template.id} value={template.id}>
                      {template.label}
                    </MenuItem>
                  ))}
                </Select>
                {announcementTemplateId ? (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    {ANNOUNCEMENT_TEMPLATES.find((t) => t.id === announcementTemplateId)?.description}
                  </Typography>
                ) : null}
              </Box>
              {announcementTemplateId === 'newFeature' && (
                <TextField
                  label="Feature name"
                  value={announcementForm.featureName}
                  onChange={(e) => handleFeatureNameChange(e.target.value)}
                  fullWidth
                  required
                  helperText="Used in the title and message. You can still edit those fields below."
                />
              )}
              <TextField
                label="Title (optional)"
                value={announcementForm.title}
                onChange={(e) => setAnnouncementForm((prev) => ({ ...prev, title: e.target.value }))}
                fullWidth
              />
              <TextField
                label="Message"
                value={announcementForm.message}
                onChange={(e) => setAnnouncementForm((prev) => ({ ...prev, message: e.target.value }))}
                fullWidth
                required
                multiline
                minRows={3}
              />
              <Select
                value={announcementForm.severity}
                onChange={(e: SelectChangeEvent) =>
                  setAnnouncementForm((prev) => ({
                    ...prev,
                    severity: e.target.value as AnnouncementSeverity,
                  }))
                }
                fullWidth
                displayEmpty
              >
                <MenuItem value="INFO">Info</MenuItem>
                <MenuItem value="WARNING">Warning</MenuItem>
                <MenuItem value="CRITICAL">Critical</MenuItem>
              </Select>
              <Select
                value={announcementForm.audience}
                onChange={(e: SelectChangeEvent) =>
                  setAnnouncementForm((prev) => ({
                    ...prev,
                    audience: e.target.value as AnnouncementAudience,
                  }))
                }
                fullWidth
              >
                <MenuItem value="ALL">All users</MenuItem>
                <MenuItem value="SPECIFIC">Specific users</MenuItem>
              </Select>
              {announcementForm.audience === 'SPECIFIC' && (
                <TextField
                  label="Target usernames"
                  helperText="Comma-separated usernames"
                  value={announcementForm.targetUsernames}
                  onChange={(e) =>
                    setAnnouncementForm((prev) => ({ ...prev, targetUsernames: e.target.value }))
                  }
                  fullWidth
                />
              )}
              <TextField
                label="Starts at (optional)"
                type="datetime-local"
                value={announcementForm.startsAt}
                onChange={(e) => handleAnnouncementWindowChange('startsAt', e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
                required={
                  announcementTemplateId === 'maintenance' ||
                  announcementTemplateId === 'systemRestart'
                }
              />
              <TextField
                label="Ends at (optional)"
                type="datetime-local"
                value={announcementForm.endsAt}
                onChange={(e) => handleAnnouncementWindowChange('endsAt', e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
                required={
                  announcementTemplateId === 'maintenance' ||
                  announcementTemplateId === 'systemRestart'
                }
              />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Switch
                  checked={announcementForm.dismissible}
                  onChange={(_, checked) =>
                    setAnnouncementForm((prev) => ({ ...prev, dismissible: checked }))
                  }
                />
                <Typography variant="body2">Users can dismiss this announcement</Typography>
              </Box>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCreateAnnouncementOpen(false)} disabled={announcementSaving}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={() => void handleCreateAnnouncement()}
              disabled={
                announcementSaving ||
                !announcementForm.message.trim() ||
                ((announcementTemplateId === 'maintenance' ||
                  announcementTemplateId === 'systemRestart') &&
                  (!announcementForm.startsAt.trim() || !announcementForm.endsAt.trim())) ||
                (announcementTemplateId === 'newFeature' && !announcementForm.featureName.trim())
              }
            >
              {announcementSaving ? 'Publishing…' : 'Publish'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={!!deleteAnnouncement} onClose={() => setDeleteAnnouncement(null)}>
          <DialogTitle sx={{ color: 'error.main' }}>Delete announcement</DialogTitle>
          <DialogContent>
            <Alert severity="warning" sx={{ mb: 2 }}>
              This permanently removes the announcement and its dismissal history.
            </Alert>
            <DialogContentText>
              Delete this announcement
              {deleteAnnouncement?.title ? (
                <>
                  {' '}
                  <strong>{deleteAnnouncement.title}</strong>
                </>
              ) : null}
              ?
            </DialogContentText>
            {deleteAnnouncement?.message ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                {deleteAnnouncement.message}
              </Typography>
            ) : null}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteAnnouncement(null)}>Cancel</Button>
            <Button color="error" variant="contained" onClick={() => void handleDeleteAnnouncement()}>
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Paper>
    </Container>
    </Box>
  );
};

export default AdminConsolePage; 